import React, { useEffect, useMemo, useState } from "react";
import {
  Search,
  Filter,
  Mail,
  UserCheck,
  Clock,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ChevronsDownUp,
  ChevronsUpDown,
  MoreVertical,
  Edit,
  ExternalLink,
  Zap,
  Building,
  Truck,
  IndianRupee,
  FileText,
  Columns,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Table as TableIcon,
  LayoutGrid,
  Maximize2,
  Calendar,
  ListFilter,
  Check,
  Sparkles,
  Plus,
  Layers,
  ArrowLeft,
  ArrowRight,
  GripVertical,
  RotateCcw,
  SlidersHorizontal,
  Eye,
  EyeOff,
  ChevronsUp,
  ChevronsDown,
  User,
  Users,
  Package,
  Phone,
  FileSpreadsheet,
  PackageCheck,
  Download,
  Trash2,
  CheckSquare,
  Square,
  MinusSquare,
} from "lucide-react";
import {
  ProjectTask,
  ProjectStage,
  PriorityLevel,
  getConcernedPersons,
  getProjectConcernedRoles,
  getProjectRoleAssignments,
  getProjectItems,
  calculateItemsTotal,
  formatItemsSummary,
  ProjectLineItem,
  getDeliveryCommitment,
  getProjectDetailsSummary,
  getPartyDetails,
  getVendorDetails,
  VendorDetailsInfo,
  StageHead,
  STAGE_HEAD_SUBHEADS,
  getProjectStageStatus,
  StageStatusDetails,
  VendorPO,
  getProjectPOs,
  calculateProjectPOsTotal,
  calculatePOItemStats,
  formatToDDMMYY,
  getDrawingTaskInfo,
  ProjectRoleAssignment,
  ProjectDocument,
} from "./types.ts";
import { TEAM_MEMBERS } from "./initialProjects.ts";
import { TeamTasksDeadlinesModal } from "./TeamTasksDeadlinesModal.tsx";
import { QuickAddRoleModal } from "./QuickAddRoleModal.tsx";
import { DeleteMultipleJobsModal } from "./DeleteMultipleJobsModal.tsx";

interface ProjectTableProps {
  projects: ProjectTask[];
  engineers?: { name: string; role?: string }[];
  onOpenEmailModal: (project: ProjectTask) => void;
  onOpenEditModal: (project: ProjectTask) => void;
  onOpenDetailSheet: (project: ProjectTask) => void;
  onUpdateStage: (
    projectId: string,
    newStage: ProjectStage,
    additionalUpdates?: Partial<ProjectTask>,
  ) => void;
  onToggleMilestone: (projectId: string, milestoneId: string) => void;
  onReassignEngineer: (projectId: string, newEngineer: string) => void;
  onUpdateConcernedPersons?: (projectId: string, persons: string[]) => void;
  onAddProjectRole?: (projectId: string, role: ProjectRoleAssignment) => void;
  onNewJob?: () => void;
  onOpenTeamProfiles?: () => void;
  onManagePOs?: (project: ProjectTask) => void;
  onOpenStoreDesk?: (project: ProjectTask, poId?: string) => void;
  isAdmin?: boolean;
  onExportCsv?: () => void;
  onDeleteProject?: (project: ProjectTask) => void;
  onDeleteMultipleProjects?: (projects: ProjectTask[]) => void;
  onOpenDocument?: (project: ProjectTask, document: ProjectDocument) => void;
  onDownloadDocument?: (project: ProjectTask, document: ProjectDocument) => void;
  headerSummary?: React.ReactNode;
}

// Full Column Definition matching DVEPL Production Tracking Sheet
export interface ColumnDef {
  id: string;
  label: string;
  category:
    | "core"
    | "engineering"
    | "commercial"
    | "procurement"
    | "dispatch"
    | "reminder";
  defaultVisible: boolean;
}

const ALL_COLUMNS: ColumnDef[] = [
  { id: "slNo", label: "S.No.", category: "core", defaultVisible: true },
  {
    id: "partyName",
    label: "Customer Name & Code",
    category: "core",
    defaultVisible: true,
  },
  {
    id: "orderTakenAndDate",
    label: "Drawing Status",
    category: "core",
    defaultVisible: true,
  },
  {
    id: "concernedPerson",
    label: "Project Concerned Persons",
    category: "engineering",
    defaultVisible: true,
  },
  {
    id: "itemDescription",
    label: "Project Details",
    category: "core",
    defaultVisible: true,
  },
  {
    id: "poNumber",
    label: "Vendor Details",
    category: "procurement",
    defaultVisible: true,
  },
  {
    id: "stage",
    label: "Stage / Status",
    category: "engineering",
    defaultVisible: true,
  },
  {
    id: "followUpCount",
    label: "Reminder # & Cadence",
    category: "reminder",
    defaultVisible: true,
  },
  {
    id: "billNumber",
    label: "Bill No & Dispatch",
    category: "dispatch",
    defaultVisible: true,
  },
  {
    id: "drawingApprovedDate",
    label: "Drg Approved Date (Separate Column)",
    category: "engineering",
    defaultVisible: false,
  },
  {
    id: "drawingSendDate",
    label: "Drg Send Date (Separate Column)",
    category: "engineering",
    defaultVisible: false,
  },
  {
    id: "deliveryTarget",
    label: "Date of Delivery (Separate Column)",
    category: "core",
    defaultVisible: false,
  },
];

const COLUMN_WIDTHS: Record<string, string> = {
  slNo: "50px",
  partyName: "300px",
  orderTakenAndDate: "230px",
  concernedPerson: "245px",
  itemDescription: "230px",
  poNumber: "250px",
  stage: "190px",
  followUpCount: "145px",
  billNumber: "185px",
};
const SELECT_COLUMN_WIDTH = "42px";
const EXPAND_COLUMN_WIDTH = "38px";
const ACTIONS_COLUMN_WIDTH = "168px";

const SHEET_STATE_KEY = "dvepl_jobs_tracking_sheet_state_v1";

export const STORAGE_KEY_COL_ORDER = "dvepl_table_col_order_v16";
export const STORAGE_KEY_COL_VISIBLE = "dvepl_table_col_visible_v16";
export const DEFAULT_COLUMN_ORDER = ALL_COLUMNS.map((c) => c.id);

// Helper to reliably parse date strings (ISO, DD-MM-YYYY, DD-MM-YY, etc.) into timestamps for accurate chronological sorting
export const parseDateToTimestamp = (
  dateStr?: string | number | null,
): number => {
  if (dateStr === null || dateStr === undefined) return -8640000000000000;
  if (typeof dateStr === "number")
    return isNaN(dateStr) ? -8640000000000000 : dateStr;
  const s = String(dateStr).trim();
  if (
    !s ||
    s === "-" ||
    s.toLowerCase() === "pending" ||
    s.toLowerCase() === "not sent" ||
    s.toLowerCase() === "n/a"
  ) {
    return -8640000000000000;
  }

  // Check DD-MM-YYYY or DD-MM-YY or DD/MM/YYYY or DD/MM/YY
  const dmyMatch = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})/);
  if (dmyMatch) {
    const day = parseInt(dmyMatch[1], 10);
    const month = parseInt(dmyMatch[2], 10) - 1;
    let year = parseInt(dmyMatch[3], 10);
    if (year < 100) year += 2000;
    const d = new Date(year, month, day);
    if (!isNaN(d.getTime())) return d.getTime();
  }

  const parsed = Date.parse(s);
  return isNaN(parsed) ? -8640000000000000 : parsed;
};

export interface CompanyGroupMeta {
  key: string;
  partyName: string;
  count: number;
  totalAmount: number;
  totalBalance: number;
  latestTimestamp: number;
  latestDateStr: string;
  caNumbers: string[];
  projects: ProjectTask[];
}

export type DisplayRowItem =
  | { type: "header"; group: CompanyGroupMeta; groupIndex: number }
  | {
      type: "project";
      project: ProjectTask;
      group?: CompanyGroupMeta;
      indexInCompany: number;
      globalIndex: number;
      customerIndex?: number;
    };

export const ProjectTable: React.FC<ProjectTableProps> = ({
  projects,
  engineers = [],
  onOpenEmailModal,
  onOpenEditModal,
  onOpenDetailSheet,
  onUpdateStage,
  onToggleMilestone,
  onReassignEngineer,
  onUpdateConcernedPersons,
  onNewJob,
  onOpenTeamProfiles,
  onManagePOs,
  onOpenStoreDesk,
  isAdmin = false,
  onExportCsv,
  onAddProjectRole,
  onDeleteProject,
  onDeleteMultipleProjects,
  onOpenDocument,
  onDownloadDocument,
  headerSummary,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEngineer, setSelectedEngineer] = useState("all");
  const [selectedStage, setSelectedStage] = useState("all");
  const [selectedPriority, setSelectedPriority] = useState("all");
  const [selectedDrawingStatus, setSelectedDrawingStatus] = useState<
    "all" | "on_time" | "due_today" | "overdue" | "completed"
  >("all");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showTeamDeadlinesModal, setShowTeamDeadlinesModal] = useState(false);
  const [quickAddRoleProject, setQuickAddRoleProject] =
    useState<ProjectTask | null>(null);
  const [activeTab, setActiveTab] = useState<
    | "all"
    | "drawing_pending"
    | "material_pending"
    | "in_production"
    | "due_today"
  >("all");
  const [expandedProjectIds, setExpandedProjectIds] = useState<Set<string>>(new Set());
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<string>>(
    new Set(),
  );
  const [isDeleteMultipleModalOpen, setIsDeleteMultipleModalOpen] =
    useState(false);

  // Dynamic Drawing tasks status summary across active projects
  const drawingStats = useMemo(() => {
    let overdue = 0;
    let dueToday = 0;
    let onTrack = 0;
    let completed = 0;
    projects.forEach((p) => {
      const info = getDrawingTaskInfo(p);
      if (info.status === "overdue") overdue++;
      else if (info.status === "due_today") dueToday++;
      else if (info.status === "on_time") onTrack++;
      else if (info.status === "completed") completed++;
    });
    return { overdue, dueToday, onTrack, completed, total: projects.length };
  }, [projects]);

  // Active PO view tab for projects with multiple POs: 'all' or PO index
  const [activePoTabByProject, setActivePoTabByProject] = useState<
    Record<string, number | "all">
  >({});

  // View mode: 'sheet' (Full Spreadsheet Master Sheet) vs 'compact' (Cards)
  const [viewMode, setViewMode] = useState<"sheet" | "compact">(() =>
    window.innerWidth < 768 ? "compact" : "sheet",
  );
  const [isDense, setIsDense] = useState(true);

  // Column visibility state (persisted in localStorage)
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    () => {
      const DELETED_COLS = [
        "caNumber",
        "dveplCode",
        "amount",
        "gst",
        "totalAmount",
        "advance",
        "balance",
        "vendorName",
        "poAmount",
      ];
      try {
        const saved =
          localStorage.getItem(STORAGE_KEY_COL_VISIBLE) ||
          localStorage.getItem("dvepl_table_col_visible_v14") ||
          localStorage.getItem("dvepl_table_col_visible_v11");
        if (saved) {
          const parsed = JSON.parse(saved);
          if (typeof parsed === "object" && parsed !== null) {
            if ("orderConfirmDate" in parsed || "orderTakenBy" in parsed) {
              parsed.orderTakenAndDate =
                parsed.orderConfirmDate !== false &&
                parsed.orderTakenBy !== false;
              delete parsed.orderConfirmDate;
              delete parsed.orderTakenBy;
            }
            // Purge deleted columns including separate dveplCode and caNumber
            DELETED_COLS.forEach((col) => {
              delete parsed[col];
            });
            // Ensure poNumber (Vendor Details) and slNo are visible
            parsed.poNumber = true;
            parsed.slNo = true;
            // Turn off separate deliveryTarget, drawingSendDate & drawingApprovedDate by default since they are now sub-headed under Order Taken By
            parsed.deliveryTarget = false;
            parsed.drawingSendDate = false;
            parsed.drawingApprovedDate = false;
            return parsed;
          }
        }
      } catch (e) {
        console.warn("Failed loading visibleColumns from localStorage:", e);
      }
      const initial: Record<string, boolean> = {};
      ALL_COLUMNS.forEach((c) => {
        initial[c.id] = c.defaultVisible !== false;
      });
      return initial;
    },
  );

  // Column ordering state (persisted in localStorage)
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const DELETED_COLS = [
      "caNumber",
      "dveplCode",
      "amount",
      "gst",
      "totalAmount",
      "advance",
      "balance",
      "vendorName",
      "poAmount",
    ];
    try {
      const saved =
        localStorage.getItem(STORAGE_KEY_COL_ORDER) ||
        localStorage.getItem("dvepl_table_col_order_v15") ||
        localStorage.getItem("dvepl_table_col_order_v14") ||
        localStorage.getItem("dvepl_table_col_order_v11");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const mapped = parsed.map((id) =>
            id === "orderConfirmDate" || id === "orderTakenBy"
              ? "orderTakenAndDate"
              : id,
          );
          const unique = Array.from(new Set(mapped));
          const validIds = unique.filter(
            (id) =>
              !DELETED_COLS.includes(id) &&
              ALL_COLUMNS.some((c) => c.id === id),
          );
          ALL_COLUMNS.forEach((c) => {
            if (!validIds.includes(c.id)) {
              validIds.push(c.id);
            }
          });
          // Ensure slNo is at index 0
          if (validIds.includes("slNo")) {
            const idx = validIds.indexOf("slNo");
            if (idx > 0) {
              validIds.splice(idx, 1);
              validIds.unshift("slNo");
            }
          } else {
            validIds.unshift("slNo");
          }
          return validIds;
        }
      }
    } catch (e) {
      console.warn("Failed loading columnOrder from localStorage:", e);
    }
    return DEFAULT_COLUMN_ORDER;
  });

  const [isColumnPickerOpen, setIsColumnPickerOpen] = useState(false);
  const [columnPickerTab, setColumnPickerTab] = useState<
    "order" | "visibility"
  >("order");
  const [columnSearchTerm, setColumnSearchTerm] = useState("");

  // Drag & drop state for column reordering
  const [draggedColId, setDraggedColId] = useState<string | null>(null);
  const [dragOverColId, setDragOverColId] = useState<string | null>(null);

  const updateColumnOrder = (newOrder: string[]) => {
    setColumnOrder(newOrder);
    try {
      localStorage.setItem(STORAGE_KEY_COL_ORDER, JSON.stringify(newOrder));
    } catch (e) {
      console.warn("Failed saving column order to localStorage:", e);
    }
  };

  const updateVisibleColumns = (
    updater:
      | Record<string, boolean>
      | ((prev: Record<string, boolean>) => Record<string, boolean>),
  ) => {
    setVisibleColumns((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      try {
        localStorage.setItem(STORAGE_KEY_COL_VISIBLE, JSON.stringify(next));
      } catch (e) {
        console.warn("Failed saving visibleColumns to localStorage:", e);
      }
      return next;
    });
  };

  const moveColumn = (
    colId: string,
    direction: "left" | "right" | "top" | "bottom",
  ) => {
    const currentIdx = columnOrder.indexOf(colId);
    if (currentIdx === -1) return;
    const newOrder = [...columnOrder];
    if (direction === "left" && currentIdx > 0) {
      const temp = newOrder[currentIdx - 1];
      newOrder[currentIdx - 1] = newOrder[currentIdx];
      newOrder[currentIdx] = temp;
    } else if (direction === "right" && currentIdx < newOrder.length - 1) {
      const temp = newOrder[currentIdx + 1];
      newOrder[currentIdx + 1] = newOrder[currentIdx];
      newOrder[currentIdx] = temp;
    } else if (direction === "top") {
      newOrder.splice(currentIdx, 1);
      newOrder.unshift(colId);
    } else if (direction === "bottom") {
      newOrder.splice(currentIdx, 1);
      newOrder.push(colId);
    }
    updateColumnOrder(newOrder);
  };

  const moveColumnToIndex = (colId: string, targetIdx: number) => {
    const currentIdx = columnOrder.indexOf(colId);
    if (currentIdx === -1 || targetIdx === currentIdx) return;
    const newOrder = [...columnOrder];
    newOrder.splice(currentIdx, 1);
    newOrder.splice(targetIdx, 0, colId);
    updateColumnOrder(newOrder);
  };

  const resetColumnOrder = () => {
    updateColumnOrder(DEFAULT_COLUMN_ORDER);
  };

  const handleHeaderDragStart = (e: React.DragEvent, colId: string) => {
    setDraggedColId(colId);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", colId);
  };

  const handleHeaderDragOver = (e: React.DragEvent, colId: string) => {
    e.preventDefault();
    if (draggedColId && draggedColId !== colId) {
      setDragOverColId(colId);
    }
  };

  const handleHeaderDragLeave = () => {
    setDragOverColId(null);
  };

  const handleHeaderDrop = (e: React.DragEvent, targetColId: string) => {
    e.preventDefault();
    setDragOverColId(null);
    if (draggedColId && draggedColId !== targetColId) {
      const targetIdx = columnOrder.indexOf(targetColId);
      if (targetIdx !== -1) {
        moveColumnToIndex(draggedColId, targetIdx);
      }
    }
    setDraggedColId(null);
  };

  // Multi-Level Sorting & Company Sequencing state
  // Default to Sequence by Company = TRUE and Latest Order Confirm Date = DESC
  const [groupByCompany, setGroupByCompany] = useState<boolean>(true);
  const [sortField, setSortField] = useState<string>("orderConfirmDate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [collapsedCompanies, setCollapsedCompanies] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    try {
      localStorage.removeItem(SHEET_STATE_KEY);
    } catch (error) {
      console.warn("Failed clearing Jobs Tracking Sheet state:", error);
    }
  }, []);

  const toggleCollapseCompany = (companyKey: string) => {
    setCollapsedCompanies((prev) => ({
      ...prev,
      [companyKey]: !prev[companyKey],
    }));
  };

  const collapseAllCompanies = () => {
    const next: Record<string, boolean> = {};
    companySummaries.forEach((c) => {
      next[c.key] = true;
    });
    setCollapsedCompanies(next);
  };

  const expandAllCompanies = () => {
    setCollapsedCompanies({});
  };

  const handleCollapseAll = () => {
    if (groupByCompany) {
      // In Sequence by Company (Active position):
      // Collapse all companies so only customer header bars are shown
      const next: Record<string, boolean> = {};
      companySummaries.forEach((c) => {
        next[c.key] = true;
      });
      setCollapsedCompanies(next);
    } else {
      // In Sequence by Company (Off position / Flat List):
      setExpandedProjectIds(new Set());
    }
  };

  const handleExpandAll = () => {
    if (groupByCompany) {
      // In Active position: expand all company sections
      setCollapsedCompanies({});
    } else {
      // In Off position: expand all project row details (milestones, line items, deep logs)
      const allIds = new Set(flatProjects.map((p) => p.id));
      setExpandedProjectIds(allIds);
    }
  };

  // Bulk Job Selection Handlers for Multi-Job Actions (Batch Deletion)
  const handleToggleSelectJob = (projectId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setSelectedProjectIds((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  const handleSelectAllVisible = () => {
    if (
      selectedProjectIds.size >= flatProjects.length &&
      flatProjects.length > 0
    ) {
      setSelectedProjectIds(new Set());
    } else {
      setSelectedProjectIds(new Set(flatProjects.map((p) => p.id)));
    }
  };

  const handleClearSelection = () => {
    setSelectedProjectIds(new Set());
  };

  const selectedProjectsList = useMemo(() => {
    return projects.filter((p) => selectedProjectIds.has(p.id));
  }, [projects, selectedProjectIds]);

  const handleDeleteSelectedProjects = (deletedIds: string[]) => {
    if (onDeleteMultipleProjects) {
      const targets = projects.filter((p) => deletedIds.includes(p.id));
      onDeleteMultipleProjects(targets);
    } else if (onDeleteProject) {
      deletedIds.forEach((id) => {
        const target = projects.find((p) => p.id === id);
        if (target) onDeleteProject(target);
      });
    }
    setSelectedProjectIds(new Set());
    setIsDeleteMultipleModalOpen(false);
  };

  const handleSort = (field: string) => {
    if (field === "slNo") {
      setSortField("");
      setSortOrder("asc");
      return;
    }

    if (field === "partyName") {
      if (!groupByCompany) {
        setGroupByCompany(true);
        setSortField("partyName");
        setSortOrder("asc");
      } else if (sortField === "partyName") {
        setSortOrder(sortOrder === "asc" ? "desc" : "asc");
      } else {
        setSortField("partyName");
        setSortOrder("asc");
      }
      return;
    }

    if (field === "orderTakenAndDate") {
      if (
        sortField === "orderConfirmDate" ||
        sortField === "orderTakenAndDate"
      ) {
        setSortOrder(sortOrder === "asc" ? "desc" : "asc");
      } else {
        setSortField("orderConfirmDate");
        setSortOrder("desc");
      }
      return;
    }

    const isDateField = [
      "orderConfirmDate",
      "orderTakenAndDate",
      "deliveryTarget",
      "drawingSendDate",
      "drawingApprovedDate",
      "lastFollowUpDate",
      "nextFollowUpDate",
      "billDate",
      "dispatchDate",
      "createdAt",
    ].includes(field);

    const isNumericField = [
      "totalItems",
      "totalQty",
      "totalAmountWithGst",
      "totalAmountWithoutGst",
      "advance",
      "balance",
      "poAmount",
      "poAmountWithGst",
      "followUpCount",
      "followUpCadenceDays",
    ].includes(field);

    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      // Dates and amounts default to descending (latest date / highest value first)
      if (isDateField || isNumericField) {
        setSortOrder("desc");
      } else {
        setSortOrder("asc");
      }
    }
  };

  const toggleColumn = (id: string) => {
    updateVisibleColumns((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleUpdateStageSubHead = (
    projectId: string,
    head: StageHead,
    subHead: string,
  ) => {
    const proj = projects.find((p) => p.id === projectId);
    const updates: Partial<ProjectTask> = {
      stageHead: head,
      stageSubHead: subHead,
    };
    let computedStage: ProjectStage = `${head} - ${subHead}`;

    if (head === "Drawings") {
      updates.stageDrawings = subHead;
      if (subHead === "Approved") {
        computedStage = "Drawing Approved";
        if (!proj?.drawingApprovedDate) {
          updates.drawingApprovedDate = new Date().toISOString().split("T")[0];
        }
      } else if (subHead === "Send") {
        computedStage = "Drawing Sent";
      }
    } else if (head === "PO") {
      updates.stagePo = subHead;
      if (subHead === "Placed") {
        computedStage = "PO Placed";
      }
    } else if (head === "Material") {
      updates.stageMaterial = subHead;
      if (subHead === "Not Recevied") {
        computedStage = "Material Pending";
      } else if (subHead === "Fully recevied") {
        computedStage = "Material - Fully recevied";
      } else {
        computedStage = "Material - Partially Recevied";
      }
    } else if (head === "Production") {
      updates.stageProduction = subHead;
      if (subHead === "Testing") computedStage = "Testing";
      else if (subHead === "Electrical") computedStage = "Electrical Wiring";
      else if (subHead === "Powder coating") computedStage = "Powder Coating";
      else if (
        subHead === "Cutter" ||
        subHead === "Laser" ||
        subHead === "welding"
      )
        computedStage = "Fabrication";
      else if (subHead === "Ready") computedStage = "Ready for Dispatch";
      else computedStage = `Production - ${subHead}`;
    }

    updates.stage = computedStage;
    onUpdateStage(projectId, computedStage, updates);
  };

  const setColumnPreset = (preset: "all" | "engineering" | "procurement") => {
    const next: Record<string, boolean> = {};
    if (preset === "all") {
      ALL_COLUMNS.forEach((c) => {
        next[c.id] = true;
      });
    } else if (preset === "engineering") {
      ALL_COLUMNS.forEach((c) => {
        next[c.id] =
          c.category === "core" ||
          c.category === "engineering" ||
          c.id === "followUpCount";
      });
    } else if (preset === "procurement") {
      ALL_COLUMNS.forEach((c) => {
        next[c.id] =
          c.category === "core" ||
          c.category === "procurement" ||
          c.id === "stage" ||
          c.id === "followUpCount";
      });
    }
    updateVisibleColumns(next);
  };

  const toggleExpand = (id: string) => {
    setExpandedProjectIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const {
    displayItems,
    flatProjects,
    companyCount,
    totalOrdersCount,
    companySummaries,
  } = useMemo(() => {
    const today = new Date("2026-09-19").getTime();

    const filtered = projects.filter((p) => {
      // Tab filter
      if (activeTab === "drawing_pending") {
        if (
          p.stage !== "Drawing Approval Pending" &&
          p.stage !== "Drawing Sent"
        )
          return false;
      } else if (activeTab === "material_pending") {
        const stg = getProjectStageStatus(p);
        const hasPendingMat =
          (p.materialStatus &&
            p.materialStatus.toLowerCase().includes("pending")) ||
          p.stage === "Material Pending" ||
          stg.material === "Not Recevied" ||
          stg.material === "Partially Recevied";
        if (!hasPendingMat) return false;
      } else if (activeTab === "in_production") {
        if (
          ![
            "Fabrication",
            "Powder Coating",
            "Electrical Wiring",
            "Testing",
          ].includes(p.stage)
        )
          return false;
      } else if (activeTab === "due_today") {
        if (!p.autoFollowUpEnabled || p.stage === "Dispatched") return false;
        if (!p.nextFollowUpDate) return true;
        if (new Date(p.nextFollowUpDate).getTime() > today) return false;
      }

      // Dropdown filters
      if (selectedEngineer !== "all") {
        const assignedPersons = getConcernedPersons(p);
        if (
          !assignedPersons.some(
            (person) => person.toLowerCase() === selectedEngineer.toLowerCase(),
          )
        ) {
          return false;
        }
      }
      if (selectedStage !== "all") {
        const stg = getProjectStageStatus(p);
        if (selectedStage.startsWith("head:")) {
          const head = selectedStage.replace("head:", "");
          if (head === "Drawings" && !stg.drawings) return false;
          if (head === "PO" && !stg.po) return false;
          if (head === "Material" && !stg.material) return false;
          if (head === "Production" && !stg.production) return false;
        } else if (selectedStage.includes(":")) {
          const [h, sub] = selectedStage.split(":");
          if (h === "Drawings" && stg.drawings !== sub) return false;
          if (h === "PO" && stg.po !== sub) return false;
          if (h === "Material" && stg.material !== sub) return false;
          if (h === "Production" && stg.production !== sub) return false;
        } else if (
          p.stage !== selectedStage &&
          stg.activeHead !== selectedStage &&
          stg.activeSubHead !== selectedStage
        ) {
          return false;
        }
      }
      if (selectedPriority !== "all" && p.priority !== selectedPriority) {
        return false;
      }

      // Drawing Task Target Date status filter (on_time, due_today, overdue, completed)
      if (selectedDrawingStatus !== "all") {
        const dtInfo = getDrawingTaskInfo(p);
        if (selectedDrawingStatus === "on_time" && dtInfo.status !== "on_time")
          return false;
        if (
          selectedDrawingStatus === "due_today" &&
          dtInfo.status !== "due_today"
        )
          return false;
        if (selectedDrawingStatus === "overdue" && dtInfo.status !== "overdue")
          return false;
        if (
          selectedDrawingStatus === "completed" &&
          dtInfo.status !== "completed"
        )
          return false;
      }

      // Search term matching any column
      if (searchTerm.trim()) {
        const query = searchTerm.toLowerCase();
        const matchesCode =
          p.dveplCode.toLowerCase().includes(query) ||
          p.sNo.toLowerCase().includes(query);
        const partyInfo = getPartyDetails(p);
        const matchesParty =
          partyInfo.companyName.toLowerCase().includes(query) ||
          partyInfo.contactPerson.toLowerCase().includes(query) ||
          partyInfo.mobileNo.toLowerCase().includes(query) ||
          partyInfo.emailId.toLowerCase().includes(query) ||
          p.partyName.toLowerCase().includes(query);
        const matchesItem = p.itemDescription.toLowerCase().includes(query);
        const matchesCa = p.caNumber.toLowerCase().includes(query);
        const matchesEngineer =
          getConcernedPersons(p).some((person) =>
            person.toLowerCase().includes(query),
          ) || p.concernedPerson.toLowerCase().includes(query);
        const matchesOrderTaken = p.orderTakenBy.toLowerCase().includes(query);
        const matchesOrderDate = (p.orderConfirmDate || "")
          .toLowerCase()
          .includes(query);
        const matchesDelivery = (p.deliveryTarget || "")
          .toLowerCase()
          .includes(query);
        const matchesDrgSend = (p.drawingSendDate || "")
          .toLowerCase()
          .includes(query);
        const matchesDrgAppr = (p.drawingApprovedDate || "")
          .toLowerCase()
          .includes(query);
        const matchesVendor =
          (p.vendorName || "").toLowerCase().includes(query) ||
          (p.vendorContactPerson || "").toLowerCase().includes(query) ||
          (p.vendorEmail || "").toLowerCase().includes(query) ||
          (p.vendorMobileNo || "").toLowerCase().includes(query);
        const matchesPo = (p.poNumber || "").toLowerCase().includes(query);
        const matchesBill = (p.billNumber || "").toLowerCase().includes(query);
        const matchesRemark = (p.remark || "").toLowerCase().includes(query);
        const stgInfo = getProjectStageStatus(p);
        const matchesStage =
          p.stage.toLowerCase().includes(query) ||
          stgInfo.drawings.toLowerCase().includes(query) ||
          stgInfo.po.toLowerCase().includes(query) ||
          stgInfo.material.toLowerCase().includes(query) ||
          stgInfo.production.toLowerCase().includes(query) ||
          stgInfo.activeHead.toLowerCase().includes(query) ||
          stgInfo.activeSubHead.toLowerCase().includes(query);

        if (
          !matchesCode &&
          !matchesParty &&
          !matchesItem &&
          !matchesCa &&
          !matchesEngineer &&
          !matchesOrderTaken &&
          !matchesOrderDate &&
          !matchesDelivery &&
          !matchesDrgSend &&
          !matchesDrgAppr &&
          !matchesVendor &&
          !matchesPo &&
          !matchesBill &&
          !matchesRemark &&
          !matchesStage
        ) {
          return false;
        }
      }

      return true;
    });

    const isDateField = (field: string) =>
      [
        "orderConfirmDate",
        "orderTakenAndDate",
        "deliveryTarget",
        "drawingSendDate",
        "drawingApprovedDate",
        "lastFollowUpDate",
        "nextFollowUpDate",
        "billDate",
        "dispatchDate",
        "createdAt",
      ].includes(field);

    const isNumericField = (field: string) =>
      [
        "amount",
        "gst",
        "totalAmount",
        "advance",
        "balance",
        "poAmount",
        "poAmountWithGst",
        "followUpCount",
        "followUpCadenceDays",
      ].includes(field);

    const compareValues = (
      a: any,
      b: any,
      field: string,
      order: "asc" | "desc",
    ) => {
      if (field === "orderTakenAndDate" || field === "orderConfirmDate") {
        const tA = parseDateToTimestamp(a.orderConfirmDate);
        const tB = parseDateToTimestamp(b.orderConfirmDate);
        if (tA === tB) return 0;
        return order === "asc" ? tA - tB : tB - tA;
      }

      if (field === "orderTakenBy") {
        const strA = String(a.orderTakenBy || "").toLowerCase();
        const strB = String(b.orderTakenBy || "").toLowerCase();
        if (strA < strB) return order === "asc" ? -1 : 1;
        if (strA > strB) return order === "asc" ? 1 : -1;
        const tA = parseDateToTimestamp(a.orderConfirmDate);
        const tB = parseDateToTimestamp(b.orderConfirmDate);
        return tB - tA;
      }

      let valA = a[field];
      let valB = b[field];

      if (field === "totalItems") {
        const cA = getProjectItems(a).length || 1;
        const cB = getProjectItems(b).length || 1;
        return order === "asc" ? cA - cB : cB - cA;
      }

      if (field === "totalQty") {
        const qA =
          getProjectItems(a).reduce(
            (sum, it) => sum + (Number(it.qty) || 0),
            0,
          ) || 1;
        const qB =
          getProjectItems(b).reduce(
            (sum, it) => sum + (Number(it.qty) || 0),
            0,
          ) || 1;
        return order === "asc" ? qA - qB : qB - qA;
      }

      if (field === "totalAmountWithGst") {
        const gA =
          Number(a.totalAmount) || Math.round(Number(a.amount || 0) * 1.18);
        const gB =
          Number(b.totalAmount) || Math.round(Number(b.amount || 0) * 1.18);
        return order === "asc" ? gA - gB : gB - gA;
      }

      if (field === "totalAmountWithoutGst") {
        const wA = Number(a.amount || 0);
        const wB = Number(b.amount || 0);
        return order === "asc" ? wA - wB : wB - wA;
      }

      if (field === "advance") {
        const advA = Number(a.advance) || 0;
        const advB = Number(b.advance) || 0;
        return order === "asc" ? advA - advB : advB - advA;
      }

      if (field === "balance") {
        const balA = Number(a.balance) || 0;
        const balB = Number(b.balance) || 0;
        return order === "asc" ? balA - balB : balB - balA;
      }

      if (field === "partyName" || field === "companyName") {
        const pA = getPartyDetails(a).companyName.toLowerCase();
        const pB = getPartyDetails(b).companyName.toLowerCase();
        return order === "asc" ? pA.localeCompare(pB) : pB.localeCompare(pA);
      }

      if (field === "contactPerson") {
        const cA = getPartyDetails(a).contactPerson.toLowerCase();
        const cB = getPartyDetails(b).contactPerson.toLowerCase();
        return order === "asc" ? cA.localeCompare(cB) : cB.localeCompare(cA);
      }

      if (field === "contactPhone" || field === "mobileNo") {
        const mA = getPartyDetails(a).mobileNo;
        const mB = getPartyDetails(b).mobileNo;
        return order === "asc" ? mA.localeCompare(mB) : mB.localeCompare(mA);
      }

      if (field === "partyEmail" || field === "emailId") {
        const eA = getPartyDetails(a).emailId.toLowerCase();
        const eB = getPartyDetails(b).emailId.toLowerCase();
        return order === "asc" ? eA.localeCompare(eB) : eB.localeCompare(eA);
      }

      if (field === "poAmountWithGst") {
        const gA =
          Number(a.poAmountWithGst) ||
          Math.round(Number(a.poAmount || 0) * 1.18);
        const gB =
          Number(b.poAmountWithGst) ||
          Math.round(Number(b.poAmount || 0) * 1.18);
        return order === "asc" ? gA - gB : gB - gA;
      }

      if (field === "stage") {
        const HEAD_WEIGHT: Record<string, number> = {
          Drawings: 100,
          PO: 200,
          Material: 300,
          Production: 400,
        };
        const SUB_WEIGHT: Record<string, number> = {
          "Drawings:Send": 10,
          "Drawings:Approved": 20,
          "PO:Ready": 10,
          "PO:Placed": 20,
          "Material:Not Recevied": 10,
          "Material:Partially Recevied": 20,
          "Material:Fully recevied": 30,
          "Production:Cutter": 10,
          "Production:Laser": 20,
          "Production:welding": 30,
          "Production:Powder coating": 40,
          "Production:Assembly": 50,
          "Production:Busbar section": 60,
          "Production:Electrical": 70,
          "Production:Testing": 80,
          "Production:Ready": 90,
        };
        const stgA = getProjectStageStatus(a);
        const stgB = getProjectStageStatus(b);
        const scoreA =
          (HEAD_WEIGHT[stgA.activeHead] || 0) +
          (SUB_WEIGHT[`${stgA.activeHead}:${stgA.activeSubHead}`] || 0);
        const scoreB =
          (HEAD_WEIGHT[stgB.activeHead] || 0) +
          (SUB_WEIGHT[`${stgB.activeHead}:${stgB.activeSubHead}`] || 0);
        return order === "asc" ? scoreA - scoreB : scoreB - scoreA;
      }

      if (isDateField(field)) {
        const tA = parseDateToTimestamp(valA);
        const tB = parseDateToTimestamp(valB);
        if (tA === tB) return 0;
        return order === "asc" ? tA - tB : tB - tA;
      }

      if (isNumericField(field)) {
        if (field === "amount") {
          const amtA = Number(a.totalAmount || a.amount) || 0;
          const amtB = Number(b.totalAmount || b.amount) || 0;
          return order === "asc" ? amtA - amtB : amtB - amtA;
        }
        const numA = Number(valA) || 0;
        const numB = Number(valB) || 0;
        return order === "asc" ? numA - numB : numB - numA;
      }

      if (field === "dveplCode" || field === "sNo") {
        const sA = String(valA || "");
        const sB = String(valB || "");
        return order === "asc"
          ? sA.localeCompare(sB, undefined, {
              numeric: true,
              sensitivity: "base",
            })
          : sB.localeCompare(sA, undefined, {
              numeric: true,
              sensitivity: "base",
            });
      }

      const strA = String(valA || "").toLowerCase();
      const strB = String(valB || "").toLowerCase();
      if (strA < strB) return order === "asc" ? -1 : 1;
      if (strA > strB) return order === "asc" ? 1 : -1;
      return 0;
    };

    // Mode A: Group by Company (All orders for the same company in continuous sequence)
    if (groupByCompany) {
      const groupMap = new Map<string, CompanyGroupMeta>();

      filtered.forEach((p) => {
        const rawName = (p.partyName || "UNKNOWN PARTY").trim();
        const normKey = rawName.toUpperCase();

        let group = groupMap.get(normKey);
        if (!group) {
          group = {
            key: normKey,
            partyName: rawName,
            count: 0,
            totalAmount: 0,
            totalBalance: 0,
            latestTimestamp: -8640000000000000,
            latestDateStr: "",
            caNumbers: [],
            projects: [],
          };
          groupMap.set(normKey, group);
        }

        group.count += 1;
        group.totalAmount += p.totalAmount || p.amount || 0;
        group.totalBalance += p.balance || 0;

        const ts = parseDateToTimestamp(p.orderConfirmDate);
        if (ts > group.latestTimestamp) {
          group.latestTimestamp = ts;
          group.latestDateStr = p.orderConfirmDate || "";
        }

        if (p.caNumber && !group.caNumbers.includes(p.caNumber)) {
          group.caNumbers.push(p.caNumber);
        }

        group.projects.push(p);
      });

      const groups = Array.from(groupMap.values());

      // 1. Sort projects INSIDE each company group
      groups.forEach((g) => {
        g.projects.sort((a, b) => {
          // If sorting by a field other than partyName, use it inside the group
          if (sortField !== "partyName") {
            const comp = compareValues(a, b, sortField, sortOrder);
            if (comp !== 0) return comp;
          }
          // Default secondary inside company: latest order confirm date first
          const tA = parseDateToTimestamp(a.orderConfirmDate);
          const tB = parseDateToTimestamp(b.orderConfirmDate);
          if (tA !== tB) return tB - tA; // newest first
          return String(a.dveplCode).localeCompare(
            String(b.dveplCode),
            undefined,
            { numeric: true },
          );
        });
      });

      // 2. Sort the COMPANY GROUPS themselves
      groups.sort((gA, gB) => {
        if (sortField === "partyName") {
          const comp = gA.partyName.localeCompare(gB.partyName);
          return sortOrder === "asc" ? comp : -comp;
        }

        if (isDateField(sortField)) {
          // Sort companies by company's latest date timestamp
          const tA = gA.latestTimestamp;
          const tB = gB.latestTimestamp;
          if (tA !== tB) {
            return sortOrder === "asc" ? tA - tB : tB - tA;
          }
          return gA.partyName.localeCompare(gB.partyName);
        }

        if (sortField === "totalAmount" || sortField === "amount") {
          const diff = gA.totalAmount - gB.totalAmount;
          return sortOrder === "asc" ? diff : -diff;
        }

        if (sortField === "balance") {
          const diff = gA.totalBalance - gB.totalBalance;
          return sortOrder === "asc" ? diff : -diff;
        }

        // Default: companies with latest order date first
        if (gA.latestTimestamp !== gB.latestTimestamp) {
          return gB.latestTimestamp - gA.latestTimestamp;
        }
        return gA.partyName.localeCompare(gB.partyName);
      });

      // Build display items
      const items: DisplayRowItem[] = [];
      const flatList: ProjectTask[] = [];
      let globalIndex = 0;

      groups.forEach((g, groupIdx) => {
        items.push({ type: "header", group: g, groupIndex: groupIdx });
        const isCollapsed = Boolean(collapsedCompanies[g.key]);

        g.projects.forEach((proj, compIdx) => {
          flatList.push(proj);
          if (!isCollapsed) {
            items.push({
              type: "project",
              project: proj,
              group: g,
              indexInCompany: compIdx,
              globalIndex: globalIndex++,
              customerIndex: groupIdx,
            });
          } else {
            globalIndex++;
          }
        });
      });

      return {
        displayItems: items,
        flatProjects: flatList,
        companyCount: groups.length,
        totalOrdersCount: filtered.length,
        companySummaries: groups,
      };
    }

    // Mode B: Flat List (Direct chronological or column-based sorting across all companies)
    filtered.sort((a, b) => {
      const comp = compareValues(a, b, sortField, sortOrder);
      if (comp !== 0) return comp;
      // Tie-breaker: latest order confirm date first
      const tA = parseDateToTimestamp(a.orderConfirmDate);
      const tB = parseDateToTimestamp(b.orderConfirmDate);
      if (tA !== tB) return tB - tA;
      return String(a.dveplCode).localeCompare(String(b.dveplCode), undefined, {
        numeric: true,
      });
    });

    const items: DisplayRowItem[] = filtered.map((proj, idx) => ({
      type: "project",
      project: proj,
      indexInCompany: idx,
      globalIndex: idx,
      customerIndex: idx,
    }));

    return {
      displayItems: items,
      flatProjects: filtered,
      companyCount: new Set(
        filtered.map((p) => p.partyName.trim().toUpperCase()),
      ).size,
      totalOrdersCount: filtered.length,
      companySummaries: [],
    };
  }, [
    projects,
    searchTerm,
    selectedEngineer,
    selectedStage,
    selectedPriority,
    selectedDrawingStatus,
    activeTab,
    sortField,
    sortOrder,
    groupByCompany,
    collapsedCompanies,
  ]);

  const filteredAndSortedProjects = flatProjects;

  // Compute workload and task metrics per team member
  const engineerTaskMetrics = useMemo(() => {
    return TEAM_MEMBERS.map((tm) => {
      const tmNameLower = tm.name.toLowerCase();
      const assignedProjects = projects.filter((p) => {
        const persons = getConcernedPersons(p);
        return persons.some((per) => per.toLowerCase() === tmNameLower);
      });

      let onTime = 0;
      let dueToday = 0;
      let overdue = 0;
      let completed = 0;

      assignedProjects.forEach((p) => {
        const info = getDrawingTaskInfo(p);
        if (info.status === "completed") completed++;
        else if (info.status === "overdue") overdue++;
        else if (info.status === "due_today") dueToday++;
        else if (info.status === "on_time") onTime++;
      });

      return {
        member: tm,
        totalJobs: assignedProjects.length,
        onTime,
        dueToday,
        overdue,
        completed,
        projects: assignedProjects,
      };
    });
  }, [projects]);

  const getStageBadgeColor = (stage: ProjectStage) => {
    switch (stage) {
      case "Drawing in Process":
      case "Drawing Sent":
        return "bg-sky-50 text-sky-700 border-sky-200";
      case "Drawing Approval Pending":
        return "bg-amber-50 text-amber-800 border-amber-300 font-bold animate-pulse";
      case "Drawing Approved":
        return "bg-teal-50 text-teal-800 border-teal-200";
      case "Material Pending":
        return "bg-rose-50 text-rose-800 border-rose-300 font-bold";
      case "Fabrication":
      case "Powder Coating":
        return "bg-purple-50 text-purple-700 border-purple-200";
      case "Electrical Wiring":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "Testing":
        return "bg-cyan-50 text-cyan-800 border-cyan-200";
      case "Ready for Dispatch":
        return "bg-emerald-50 text-emerald-800 border-emerald-300 font-bold";
      case "Dispatched":
        return "bg-slate-100 text-slate-600 border-slate-200";
      case "Case on Hold":
        return "bg-red-50 text-red-800 border-red-200 font-semibold";
      default:
        return "bg-slate-100 text-slate-700 border-slate-200";
    }
  };

  const getDrawingCruxBadge = (p: ProjectTask) => {
    if (p.drawingApprovedDate) {
      return {
        text: `Drg Approved (${formatToDDMMYY(p.drawingApprovedDate)})`,
        short: "Approved",
        className: "bg-emerald-50 text-emerald-800 border-emerald-300",
      };
    }
    if (p.drawingSendDate) {
      return {
        text: `Drg Sent (${formatToDDMMYY(p.drawingSendDate)})`,
        short: "Sent / Appr Due",
        className: "bg-amber-50 text-amber-800 border-amber-300",
      };
    }
    return {
      text: "Drg Pending",
      short: "Pending",
      className: "bg-slate-100 text-slate-700 border-slate-300",
    };
  };

  const renderSortIcon = (field: string) => {
    if (sortField !== field) {
      return (
        <ArrowUpDown className="w-3 h-3 text-slate-300 opacity-60 ml-1 inline" />
      );
    }
    return sortOrder === "asc" ? (
      <ArrowUp className="w-3 h-3 text-emerald-600 ml-1 inline" />
    ) : (
      <ArrowDown className="w-3 h-3 text-emerald-600 ml-1 inline" />
    );
  };

  const renderColumnHeader = (
    colId: string,
    colIdx: number,
    totalVisible: number,
  ) => {
    const colDef = ALL_COLUMNS.find((c) => c.id === colId);
    if (!colDef) return null;

    const simpleLabels: Record<string, string> = {
      slNo: "S.NO.",
      partyName: "CUSTOMER / COMPANY & CODE",
      orderTakenAndDate: "DRAWING STATUS",
      concernedPerson: "PROJECT CONCERNED PERSONS",
      itemDescription: "PROJECT DETAILS",
      poNumber: "VENDOR DETAILS",
      stage: "STAGE / STATUS",
      followUpCount: "REMINDERS",
      billNumber: "BILL / DISPATCH",
    };

    return (
      <th
        key={`th-${colId}`}
        onClick={() => handleSort(colId)}
        className="cursor-pointer border-r border-slate-200 bg-white px-3 py-3 text-left text-[11px] font-extrabold uppercase tracking-wide text-slate-800 transition-colors hover:bg-slate-50"
        title={`Sort by ${simpleLabels[colId] || colDef.label}`}
      >
        <span className="block truncate">
          {simpleLabels[colId] || colDef.label}
        </span>
      </th>
    );

    const isFirst = colIdx === 0;
    const isLast = colIdx === totalVisible - 1;
    const isDraggingThis = draggedColId === colId;
    const isDragOverThis = dragOverColId === colId;

    let headerContent: React.ReactNode = null;
    let thClassExtra = "";

    switch (colId) {
      case "slNo":
        thClassExtra = "w-16 min-w-[58px] text-center text-slate-800";
        headerContent = (
          <div className="flex items-center justify-center space-x-1 font-extrabold text-[11px] text-slate-800 tracking-wide">
            <span>S.NO.</span>
            {renderSortIcon("slNo")}
          </div>
        );
        break;

      case "dveplCode":
        thClassExtra = "min-w-[125px]";
        headerContent = (
          <div className="flex items-center space-x-1">
            <span>DVEPL CODE</span>
            {renderSortIcon("dveplCode")}
          </div>
        );
        break;

      case "orderTakenAndDate":
      case "orderConfirmDate":
      case "orderTakenBy":
        thClassExtra = "min-w-[340px] text-emerald-900";
        headerContent = (
          <div className="flex flex-col text-left py-0.5 leading-tight select-none">
            {/* DRAWING STATUS ON TOP */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleSort("orderTakenBy");
              }}
              className="flex items-center space-x-1.5 font-bold text-slate-800 hover:text-emerald-700 text-[11px] cursor-pointer group/taken"
              title="Drawing Status (Order Taken By & Timeline Dates) - Click to sort"
            >
              <FileText className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              <span className="group-hover/taken:underline decoration-emerald-400 font-extrabold tracking-wide">
                DRAWING STATUS
              </span>
              {sortField === "orderTakenBy" && renderSortIcon("orderTakenBy")}
            </button>

            {/* SUB HEADS: CONFIRM DATE | DATE OF DELIVERY | DRG SEND DATE | DRG APPR DATE */}
            <div className="flex items-center space-x-1.5 mt-1 pt-1 border-t border-slate-200/80 text-[9.5px]">
              {/* SUB HEAD 1: CONFIRM DATE */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSort("orderConfirmDate");
                }}
                className="flex items-center space-x-0.5 font-bold text-slate-700 hover:text-emerald-700 cursor-pointer group/date"
                title="Click to sort chronologically by Order Confirm Date"
              >
                <Calendar className="w-2.5 h-2.5 text-emerald-600 shrink-0" />
                <span className="group-hover/date:underline decoration-emerald-500 uppercase tracking-tight">
                  Confirm
                </span>
                {(sortField === "orderConfirmDate" ||
                  sortField === "orderTakenAndDate") &&
                  renderSortIcon("orderConfirmDate")}
                {(sortField === "orderConfirmDate" ||
                  sortField === "orderTakenAndDate") && (
                  <span className="ml-0.5 px-1 py-0.2 rounded text-[7.5px] font-extrabold bg-emerald-600 text-white">
                    {sortOrder === "desc" ? "LATEST" : "OLDEST"}
                  </span>
                )}
              </button>

              <span className="text-slate-300 font-normal">|</span>

              {/* SUB HEAD 2: DATE OF DELIVERY */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSort("deliveryTarget");
                }}
                className="flex items-center space-x-0.5 font-bold text-slate-700 hover:text-emerald-700 cursor-pointer group/deliv"
                title="Click to sort chronologically by Date of Delivery"
              >
                <Clock className="w-2.5 h-2.5 text-amber-600 shrink-0" />
                <span className="group-hover/deliv:underline decoration-emerald-500 uppercase tracking-tight">
                  Delivery
                </span>
                {sortField === "deliveryTarget" &&
                  renderSortIcon("deliveryTarget")}
                {sortField === "deliveryTarget" && (
                  <span className="ml-0.5 px-1 py-0.2 rounded text-[7.5px] font-extrabold bg-emerald-600 text-white">
                    {sortOrder === "desc" ? "LATEST" : "EARLIEST"}
                  </span>
                )}
              </button>

              <span className="text-slate-300 font-normal">|</span>

              {/* SUB HEAD 3: DRG SEND DATE */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSort("drawingSendDate");
                }}
                className="flex items-center space-x-0.5 font-bold text-slate-700 hover:text-emerald-700 cursor-pointer group/drg"
                title="Click to sort chronologically by Drawing Send Date"
              >
                <FileText className="w-2.5 h-2.5 text-emerald-600 shrink-0" />
                <span className="group-hover/drg:underline decoration-emerald-500 uppercase tracking-tight">
                  Drg Send
                </span>
                {sortField === "drawingSendDate" &&
                  renderSortIcon("drawingSendDate")}
                {sortField === "drawingSendDate" && (
                  <span className="ml-0.5 px-1 py-0.2 rounded text-[7.5px] font-extrabold bg-emerald-600 text-white">
                    {sortOrder === "desc" ? "LATEST" : "EARLIEST"}
                  </span>
                )}
              </button>

              <span className="text-slate-300 font-normal">|</span>

              {/* SUB HEAD 4: DRG APPR DATE */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSort("drawingApprovedDate");
                }}
                className="flex items-center space-x-0.5 font-bold text-slate-700 hover:text-teal-700 cursor-pointer group/appr"
                title="Click to sort chronologically by Drawing Approved Date"
              >
                <CheckCircle2 className="w-2.5 h-2.5 text-teal-600 shrink-0" />
                <span className="group-hover/appr:underline decoration-teal-500 uppercase tracking-tight">
                  Drg Appr
                </span>
                {sortField === "drawingApprovedDate" &&
                  renderSortIcon("drawingApprovedDate")}
                {sortField === "drawingApprovedDate" && (
                  <span className="ml-0.5 px-1 py-0.2 rounded text-[7.5px] font-extrabold bg-teal-600 text-white">
                    {sortOrder === "desc" ? "LATEST" : "EARLIEST"}
                  </span>
                )}
              </button>
            </div>
          </div>
        );
        break;

      case "deliveryTarget":
        thClassExtra = "min-w-[140px]";
        headerContent = (
          <div className="flex items-center space-x-1">
            <span>DATE OF DELIVERY</span>
            {renderSortIcon("deliveryTarget")}
          </div>
        );
        break;

      case "concernedPerson":
        thClassExtra = "min-w-[260px] text-emerald-900";
        headerContent = (
          <div className="flex flex-col text-left py-0.5 leading-tight select-none">
            <div className="flex items-center space-x-1.5 font-bold text-slate-800 text-[11px]">
              <Users className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              <span className="font-extrabold tracking-wide">
                PROJECT CONCERNED PERSONS
              </span>
              {renderSortIcon("concernedPerson")}
            </div>
            <div className="text-[9px] text-slate-500 font-semibold tracking-tight mt-0.5">
              1. PO • 2. Drawings • 3. Design • 4. Production
            </div>
          </div>
        );
        break;

      case "partyName":
        thClassExtra = "min-w-[280px] max-w-[340px] text-slate-900";
        headerContent = (
          <div className="flex flex-col text-left py-0.5 leading-tight select-none">
            <div className="flex items-center space-x-1.5 font-bold text-slate-800 text-[11px]">
              <Building className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              <span className="font-extrabold tracking-wide">
                CUSTOMER / COMPANY & CODE
              </span>
              {renderSortIcon("partyName")}
              {groupByCompany && (
                <span className="ml-1 px-1.5 py-0.2 rounded text-[9px] font-extrabold bg-emerald-600 text-white">
                  SEQUENCE
                </span>
              )}
            </div>
            {/* 4 Requested Sub Heads: Company name & Code, Contact Person, Mobile no, email id */}
            <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 mt-1 pt-1 border-t border-slate-200 text-[8.5px] font-semibold text-slate-500">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSort("partyName");
                }}
                className="flex items-center space-x-1 hover:text-emerald-700 cursor-pointer text-left"
                title="Sort by Customer / Company name & Code"
              >
                <span className="w-1 h-1 rounded-full bg-emerald-500 shrink-0"></span>
                <span className="truncate">1. Customer & Code</span>
                {sortField === "partyName" && renderSortIcon("partyName")}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSort("contactPerson");
                }}
                className="flex items-center space-x-1 hover:text-emerald-700 cursor-pointer text-left"
                title="Sort by Contact Person"
              >
                <span className="w-1 h-1 rounded-full bg-emerald-500 shrink-0"></span>
                <span className="truncate">2. Contact Person</span>
                {sortField === "contactPerson" &&
                  renderSortIcon("contactPerson")}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSort("contactPhone");
                }}
                className="flex items-center space-x-1 hover:text-emerald-700 cursor-pointer text-left"
                title="Sort by Mobile no"
              >
                <span className="w-1 h-1 rounded-full bg-emerald-500 shrink-0"></span>
                <span className="truncate">3. Mobile no</span>
                {(sortField === "contactPhone" || sortField === "mobileNo") &&
                  renderSortIcon("contactPhone")}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSort("partyEmail");
                }}
                className="flex items-center space-x-1 hover:text-purple-700 cursor-pointer text-left"
                title="Sort by email id"
              >
                <span className="w-1 h-1 rounded-full bg-purple-500 shrink-0"></span>
                <span className="truncate">4. email id</span>
                {(sortField === "partyEmail" || sortField === "emailId") &&
                  renderSortIcon("partyEmail")}
              </button>
            </div>
          </div>
        );
        break;

      case "itemDescription":
        thClassExtra = "min-w-[260px] max-w-[320px] text-slate-900";
        headerContent = (
          <div className="flex flex-col text-left py-0.5 leading-tight select-none">
            <div className="flex items-center space-x-1.5 font-bold text-slate-800 text-[11px]">
              <Package className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              <span className="font-extrabold tracking-wide">
                PROJECT DETAILS
              </span>
              {renderSortIcon("itemDescription")}
            </div>
            {/* 6 Sub-heads matching user specification */}
            <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 mt-1 pt-1 border-t border-slate-200 text-[8.5px] font-semibold text-slate-500">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSort("totalItems");
                }}
                className="flex items-center space-x-1 hover:text-emerald-700 cursor-pointer text-left"
                title="Sort by Total Items"
              >
                <span className="w-1 h-1 rounded-full bg-emerald-500 shrink-0"></span>
                <span className="truncate">1. Total Items</span>
                {sortField === "totalItems" && renderSortIcon("totalItems")}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSort("totalQty");
                }}
                className="flex items-center space-x-1 hover:text-emerald-700 cursor-pointer text-left"
                title="Sort by Total Quantity"
              >
                <span className="w-1 h-1 rounded-full bg-emerald-500 shrink-0"></span>
                <span className="truncate">2. Total Qty</span>
                {sortField === "totalQty" && renderSortIcon("totalQty")}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSort("totalAmountWithGst");
                }}
                className="flex items-center space-x-1 hover:text-emerald-700 cursor-pointer text-left"
                title="Sort by Total Amount with GST"
              >
                <span className="w-1 h-1 rounded-full bg-emerald-500 shrink-0"></span>
                <span className="truncate">3. Amt with GST</span>
                {sortField === "totalAmountWithGst" &&
                  renderSortIcon("totalAmountWithGst")}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSort("totalAmountWithoutGst");
                }}
                className="flex items-center space-x-1 hover:text-slate-800 cursor-pointer text-left"
                title="Sort by Total Amount without GST"
              >
                <span className="w-1 h-1 rounded-full bg-slate-400 shrink-0"></span>
                <span className="truncate">4. Amt w/o GST</span>
                {sortField === "totalAmountWithoutGst" &&
                  renderSortIcon("totalAmountWithoutGst")}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSort("advance");
                }}
                className="flex items-center space-x-1 hover:text-teal-700 cursor-pointer text-left"
                title="Sort by Advance"
              >
                <span className="w-1 h-1 rounded-full bg-teal-500 shrink-0"></span>
                <span className="truncate">5. Advance</span>
                {sortField === "advance" && renderSortIcon("advance")}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSort("balance");
                }}
                className="flex items-center space-x-1 hover:text-amber-700 cursor-pointer text-left"
                title="Sort by Balance"
              >
                <span className="w-1 h-1 rounded-full bg-amber-500 shrink-0"></span>
                <span className="truncate">6. Balance</span>
                {sortField === "balance" && renderSortIcon("balance")}
              </button>
            </div>
          </div>
        );
        break;

      case "drawingSendDate":
        thClassExtra = "min-w-[120px]";
        headerContent = (
          <div className="flex items-center space-x-1">
            <span>DRG SEND DATE</span>
            {renderSortIcon("drawingSendDate")}
          </div>
        );
        break;

      case "drawingApprovedDate":
        thClassExtra = "min-w-[135px]";
        headerContent = (
          <div className="flex items-center space-x-1">
            <span>DRG APPROVED DATE</span>
            {renderSortIcon("drawingApprovedDate")}
          </div>
        );
        break;

      case "poNumber":
      case "vendorDetails":
        thClassExtra = "min-w-[260px] max-w-[320px] text-slate-900";
        headerContent = (
          <div className="flex flex-col text-left py-0.5 leading-tight select-none">
            <div className="flex items-center space-x-1.5 font-bold text-slate-800 text-[11px]">
              <Truck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              <span className="font-extrabold tracking-wide">
                VENDOR DETAILS
              </span>
              {renderSortIcon("vendorName")}
            </div>
            {/* Quick Sorts for Vendor Details */}
            <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 mt-1 pt-1 border-t border-slate-200 text-[8.5px] font-semibold text-slate-500">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSort("vendorName");
                }}
                className="flex items-center space-x-1 hover:text-emerald-700 cursor-pointer text-left"
                title="Sort by Vender Name"
              >
                <span className="w-1 h-1 rounded-full bg-emerald-500 shrink-0"></span>
                <span className="truncate">1. Vender Name</span>
                {sortField === "vendorName" && renderSortIcon("vendorName")}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSort("poNumber");
                }}
                className="flex items-center space-x-1 hover:text-emerald-700 cursor-pointer text-left"
                title="Sort by PO Number"
              >
                <span className="w-1 h-1 rounded-full bg-emerald-500 shrink-0"></span>
                <span className="truncate">2. PO Number</span>
                {sortField === "poNumber" && renderSortIcon("poNumber")}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSort("poAmount");
                }}
                className="flex items-center space-x-1 hover:text-slate-800 cursor-pointer text-left"
                title="Sort by Amount without GST"
              >
                <span className="w-1 h-1 rounded-full bg-slate-400 shrink-0"></span>
                <span className="truncate">3. Amt w/o GST</span>
                {sortField === "poAmount" && renderSortIcon("poAmount")}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSort("poAmountWithGst");
                }}
                className="flex items-center space-x-1 hover:text-emerald-700 cursor-pointer text-left"
                title="Sort by Amount with GST"
              >
                <span className="w-1 h-1 rounded-full bg-emerald-500 shrink-0"></span>
                <span className="truncate">4. With GST</span>
                {sortField === "poAmountWithGst" &&
                  renderSortIcon("poAmountWithGst")}
              </button>
            </div>
          </div>
        );
        break;

      case "stage":
        thClassExtra = "min-w-[280px] max-w-[340px] text-slate-900";
        headerContent = (
          <div className="flex flex-col text-left py-0.5 leading-tight select-none">
            <div
              className="flex items-center space-x-1.5 font-bold text-slate-800 text-[11px] cursor-pointer hover:text-emerald-700"
              onClick={() => handleSort("stage")}
              title="Click to sort by Stage sequence (Drawings -> PO -> Material -> Production)"
            >
              <Layers className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              <span className="font-extrabold tracking-wide">
                STAGE / STATUS
              </span>
              {renderSortIcon("stage")}
            </div>
            <div className="grid grid-cols-4 gap-1 mt-1 pt-1 border-t border-slate-200 text-[9px] font-bold text-slate-600">
              <span className="text-sky-700 bg-sky-50 px-1 py-0.5 rounded text-center truncate">
                1. Drawings
              </span>
              <span className="text-purple-700 bg-purple-50 px-1 py-0.5 rounded text-center truncate">
                2. PO
              </span>
              <span className="text-amber-700 bg-amber-50 px-1 py-0.5 rounded text-center truncate">
                3. Material
              </span>
              <span className="text-emerald-700 bg-emerald-50 px-1 py-0.5 rounded text-center truncate">
                4. Production
              </span>
            </div>
          </div>
        );
        break;

      case "followUpCount":
        thClassExtra = "text-center min-w-[150px]";
        headerContent = (
          <div className="flex items-center justify-center space-x-1">
            <span>REMINDERS & CADENCE</span>
            {renderSortIcon("followUpCount")}
          </div>
        );
        break;

      case "billNumber":
        thClassExtra = "min-w-[150px]";
        headerContent = (
          <div className="flex items-center space-x-1">
            <span>BILL NO & DISPATCH</span>
            {renderSortIcon("billNumber")}
          </div>
        );
        break;

      default:
        headerContent = <span>{colDef.label}</span>;
    }

    return (
      <th
        key={`th-${colId}`}
        draggable
        onDragStart={(e) => handleHeaderDragStart(e, colId)}
        onDragOver={(e) => handleHeaderDragOver(e, colId)}
        onDragLeave={handleHeaderDragLeave}
        onDrop={(e) => handleHeaderDrop(e, colId)}
        className={`py-2.5 px-2 whitespace-nowrap border-r border-slate-200 select-none group/th transition-all relative ${
          isDragOverThis
            ? "border-l-4 border-l-emerald-600 bg-emerald-100/90 shadow-inner"
            : "bg-slate-100/90 hover:bg-slate-200/80"
        } ${isDraggingThis ? "opacity-40 ring-2 ring-emerald-400" : ""} ${thClassExtra}`}
      >
        <div className="flex items-center justify-between gap-1">
          {/* Quick Move Left arrow */}
          <button
            type="button"
            disabled={isFirst}
            onClick={(e) => {
              e.stopPropagation();
              moveColumn(colId, "left");
            }}
            className={`p-0.5 rounded text-slate-400 hover:text-emerald-700 hover:bg-slate-300/70 opacity-0 group-hover/th:opacity-100 transition-opacity cursor-pointer ${
              isFirst ? "invisible pointer-events-none" : ""
            }`}
            title={`Move "${colDef.label}" column left`}
          >
            <ArrowLeft className="w-3 h-3" />
          </button>

          {/* Draggable header title (Clicking sorts, dragging moves) */}
          <div
            onClick={() => handleSort(colId)}
            className="cursor-pointer flex-1 py-0.5 flex items-center justify-center gap-1 font-bold text-[11px]"
            title={`Drag to reorder column or click to sort by ${colDef.label}`}
          >
            <GripVertical className="w-3 h-3 text-slate-300 opacity-0 group-hover/th:opacity-80 shrink-0 cursor-grab active:cursor-grabbing" />
            {headerContent}
          </div>

          {/* Quick Move Right arrow */}
          <button
            type="button"
            disabled={isLast}
            onClick={(e) => {
              e.stopPropagation();
              moveColumn(colId, "right");
            }}
            className={`p-0.5 rounded text-slate-400 hover:text-emerald-700 hover:bg-slate-300/70 opacity-0 group-hover/th:opacity-100 transition-opacity cursor-pointer ${
              isLast ? "invisible pointer-events-none" : ""
            }`}
            title={`Move "${colDef.label}" column right`}
          >
            <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </th>
    );
  };

  const renderColumnCell = (
    colId: string,
    proj: ProjectTask,
    rowPadding: string,
    item?: DisplayRowItem & { type: "project" },
  ) => {
    switch (colId) {
      case "slNo": {
        const slNumber = item ? item.globalIndex + 1 : 1;
        return (
          <td
            key={`${proj.id}-slNo`}
            className={`${rowPadding} px-0 py-3 align-top border-r border-slate-200 text-center font-mono text-xs font-black text-slate-700 bg-slate-50/50 whitespace-nowrap`}
          >
            <span className="inline-flex min-w-[28px] items-center justify-center font-extrabold text-[11px] text-slate-800">
              {slNumber}
            </span>
          </td>
        );
      }

      case "dveplCode":
        return (
          <td
            key={`${proj.id}-dveplCode`}
            className={`${rowPadding} px-3 border-r border-slate-200 font-mono font-bold whitespace-nowrap`}
          >
            <button
              type="button"
              onClick={() => onOpenDetailSheet(proj)}
              className="flex items-center space-x-1.5 text-emerald-700 hover:text-emerald-900 group cursor-pointer text-left"
              title="Click to view full detail sheet & items breakdown"
            >
              <span className="underline decoration-emerald-300 group-hover:decoration-emerald-700 text-emerald-900 group-hover:text-emerald-700 font-black underline-offset-2">
                {proj.dveplCode}
              </span>
              {proj.priority === "Critical" && (
                <span
                  className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"
                  title="Critical Priority"
                />
              )}
              <ExternalLink className="w-3 h-3 text-slate-400 group-hover:text-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          </td>
        );

      case "orderTakenAndDate":
      case "orderConfirmDate":
      case "orderTakenBy": {
        const commitment = getDeliveryCommitment(
          proj.deliveryTarget,
          proj.drawingApprovedDate,
          proj.drawingSendDate,
        );

        return (
          <td
            key={`${proj.id}-orderTakenAndDate`}
            className={`${rowPadding} px-3 py-3 align-top border-r border-slate-200 whitespace-nowrap`}
          >
            {/* ORDER TAKEN BY ON TOP */}
            <div className="font-bold text-slate-900 text-xs flex items-center space-x-1.5 leading-snug">
              <span className="text-[9.5px] font-bold text-slate-400 uppercase tracking-tight">
                Taken By:
              </span>
              <span
                className="truncate max-w-[280px]"
                title={`Order Taken By: ${proj.orderTakenBy || "N/A"}`}
              >
                {proj.orderTakenBy || "-"}
              </span>
            </div>

            {/* DATES & DRG STATUS WITH SUB-HEADS */}
            <div className="mt-1 space-y-0.5 text-[11px] font-mono leading-snug">
              {/* SUB HEAD: CONFIRM DATE */}
              <div className="flex items-center space-x-1.5 text-slate-600">
                <span className="text-[9.5px] font-semibold text-slate-400 uppercase tracking-tight w-16 shrink-0">
                  Confirm:
                </span>
                <span className="text-slate-700">
                  {formatToDDMMYY(proj.orderConfirmDate) || "-"}
                </span>
              </div>

              {/* SUB HEAD: DATE OF DELIVERY (CALCULATED/COMMITTED WHEN DRG APPROVED) */}
              <div className="flex items-start space-x-1.5 text-slate-600">
                <span className="text-[9.5px] font-semibold text-amber-700/90 uppercase tracking-tight w-16 shrink-0 pt-0.5">
                  Delivery:
                </span>
                <div className="flex flex-col">
                  <div className="flex items-center space-x-1.5">
                    <span
                      className={`font-semibold ${commitment.isCalculated ? "text-emerald-700 font-bold" : "text-slate-900"}`}
                    >
                      {commitment.displayDate}
                    </span>
                    {commitment.isCalculated && (
                      <span
                        className="inline-flex items-center px-1 py-0.2 rounded text-[8px] font-extrabold bg-emerald-100 text-emerald-800 border border-emerald-300"
                        title={commitment.subLabel}
                      >
                        Committed
                      </span>
                    )}
                    {!commitment.isCalculated &&
                      commitment.status === "awaiting_approval" && (
                        <span className="inline-flex items-center px-1 py-0.2 rounded text-[8px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                          Awaiting Appr
                        </span>
                      )}
                  </div>
                  {commitment.isCalculated && (
                    <span className="text-[8.5px] text-emerald-600 font-sans tracking-tight">
                      {commitment.subLabel}
                    </span>
                  )}
                </div>
              </div>

              {/* SUB HEAD: DRG TARGET / SEND */}
              <div className="flex items-center space-x-1.5 text-slate-600">
                <span className="text-[9.5px] font-semibold text-emerald-600 uppercase tracking-tight w-16 shrink-0">
                  Drg Target:
                </span>
                <div className="flex items-center space-x-1">
                  <span className="text-slate-700 font-medium">
                    {formatToDDMMYY(
                      proj.drawingTargetDate || proj.drawingSendDate,
                    ) || "Not Set"}
                  </span>
                  {(() => {
                    const dtInfo = getDrawingTaskInfo(proj);
                    if (dtInfo.status === "not_set") return null;
                    return (
                      <span
                        className={`px-1 py-0.2 rounded text-[8px] font-bold border ${
                          dtInfo.color === "emerald"
                            ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                            : dtInfo.color === "amber"
                              ? "bg-amber-50 text-amber-700 border-amber-300"
                              : dtInfo.color === "rose"
                                ? "bg-rose-50 text-rose-700 border-rose-300"
                                : "bg-emerald-50 text-emerald-700 border-emerald-300"
                        }`}
                        title={`Drawing Task Target: ${dtInfo.targetDateStr} (${dtInfo.label})`}
                      >
                        {dtInfo.label}
                      </span>
                    );
                  })()}
                </div>
              </div>

              {/* SUB HEAD: DRG APPR DATE */}
              <div className="flex items-center space-x-1.5 text-slate-600">
                <span className="text-[9.5px] font-semibold text-teal-700 uppercase tracking-tight w-16 shrink-0">
                  Drg Appr:
                </span>
                {proj.drawingApprovedDate ? (
                  <span
                    className="inline-flex items-center space-x-1 px-1.5 py-0.2 rounded text-[9.5px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200"
                    title={`Drawings Approved on ${formatToDDMMYY(proj.drawingApprovedDate)}. Delivery commitment active.`}
                  >
                    <CheckCircle2 className="w-2.5 h-2.5 text-emerald-600 shrink-0" />
                    <span>{formatToDDMMYY(proj.drawingApprovedDate)}</span>
                  </span>
                ) : (
                  <span className="inline-flex items-center px-1.5 py-0.2 rounded text-[8.5px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
                    {proj.drawingSendDate ? "Approval Awaited" : "Pending"}
                  </span>
                )}
              </div>
            </div>
          </td>
        );
      }

      case "deliveryTarget":
        return (
          <td
            key={`${proj.id}-deliveryTarget`}
            className={`${rowPadding} px-3 border-r border-slate-200 text-slate-700 whitespace-nowrap`}
          >
            {formatToDDMMYY(proj.deliveryTarget)}
          </td>
        );

      case "concernedPerson": {
        const allRoles = getProjectRoleAssignments(proj);
        const taskInfo = getDrawingTaskInfo(proj);

        return (
          <td
            key={`${proj.id}-concernedPerson`}
            className={`${rowPadding} px-3 py-3 align-top border-r border-slate-200`}
          >
            <div className="flex flex-col space-y-1 text-[11px] leading-snug">
              {allRoles.map((r, idx) => {
                const roleLower = r.role.toLowerCase();
                const isPO = roleLower.includes("po");
                const isDrg = roleLower.includes("draw");
                const isDesign = roleLower.includes("design");
                const isProd =
                  roleLower.includes("prod") || roleLower.includes("cut");
                const isQC =
                  roleLower.includes("test") || roleLower.includes("qual");

                const dotColor = isPO
                  ? "bg-purple-600"
                  : isDrg
                    ? "bg-emerald-600"
                    : isDesign
                      ? "bg-emerald-600"
                      : isProd
                        ? "bg-amber-600"
                        : isQC
                          ? "bg-emerald-600"
                          : "bg-teal-600";

                const titleColor = isPO
                  ? "text-purple-700"
                  : isDrg
                    ? "text-emerald-700"
                    : isDesign
                      ? "text-emerald-700"
                      : isProd
                        ? "text-amber-700"
                        : isQC
                          ? "text-emerald-700"
                          : "text-teal-700";

                return (
                  <div
                    key={`${proj.id}-role-${idx}-${r.role}`}
                    className="flex items-center justify-between space-x-2"
                  >
                    <span
                      className={`text-[9.5px] font-bold ${titleColor} uppercase tracking-tight shrink-0 flex items-center space-x-1`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${dotColor}`}
                      ></span>
                      <span className="truncate max-w-[115px]">
                        {idx + 1}. {r.role}:
                      </span>
                    </span>
                    <div className="flex items-center space-x-1 min-w-0 justify-end">
                      <span
                        className={`font-semibold truncate text-right ${r.person && r.person !== "-" ? "text-slate-900 font-bold" : "text-slate-400"}`}
                        title={`${r.role}: ${r.person}`}
                      >
                        {r.person || "-"}
                      </span>
                      {isDrg && taskInfo.status !== "not_set" && (
                        <span
                          className={`text-[8px] font-bold px-1 py-0.2 rounded shrink-0 border ${
                            taskInfo.color === "emerald"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                              : taskInfo.color === "amber"
                                ? "bg-amber-50 text-amber-700 border-amber-300"
                                : taskInfo.color === "rose"
                                  ? "bg-rose-50 text-rose-700 border-rose-300"
                                  : "bg-emerald-50 text-emerald-700 border-emerald-300"
                          }`}
                          title={`Drawing Task Deadline: ${taskInfo.targetDateStr} (${taskInfo.label})`}
                        >
                          {taskInfo.status === "completed"
                            ? "Drg Done"
                            : taskInfo.status === "overdue"
                              ? `Overdue ${Math.abs(taskInfo.daysRemainingOrOverdue)}d`
                              : taskInfo.status === "due_today"
                                ? "Due Today"
                                : `${taskInfo.daysRemainingOrOverdue}d left`}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Quick action to add / edit Core Incharge Roles */}
            <div className="pt-1 mt-0.5 border-t border-slate-100 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setQuickAddRoleProject(proj)}
                className="text-[9.5px] font-bold text-emerald-700 hover:text-emerald-900 flex items-center space-x-0.5 cursor-pointer bg-emerald-50/80 hover:bg-emerald-100 px-1.5 py-0.5 rounded border border-emerald-200 transition-colors shadow-2xs"
                title="Add Core Incharge Role to this job (will display under Project Concerned Persons on main page)"
              >
                <Plus className="w-2.5 h-2.5 mr-0.5" />
                <span>+ Add Core Incharge Role</span>
              </button>

              <button
                type="button"
                onClick={() => onOpenDetailSheet(proj)}
                className="text-[9px] text-slate-500 hover:text-slate-800 font-medium cursor-pointer"
                title="Open Job Detail Sheet"
              >
                Detail Sheet
              </button>
            </div>
          </td>
        );
      }

      case "partyName": {
        const party = getPartyDetails(proj);
        const isGroupedChild = Boolean(groupByCompany && item?.group);

        if (isGroupedChild) {
          return (
            <td
              key={`${proj.id}-partyName`}
              className={`${rowPadding} border-r border-slate-200 px-3 py-3 align-top
                text-slate-800`}
              data-testid={`grouped-project-identifier-${proj.id}`}
            >
              <div className="space-y-1.5 text-[11px] leading-snug">
                <button
                  type="button"
                  onClick={() => onOpenDetailSheet(proj)}
                  className="inline-flex items-center gap-1.5 font-mono text-[10.5px]
                    font-black text-emerald-900"
                  data-testid={`grouped-project-code-${proj.id}`}
                  title={`Open job DVEPL-${proj.dveplCode}`}
                >
                  <span>DVEPL-{proj.dveplCode}</span>
                  <ExternalLink className="h-3 w-3 text-emerald-600" />
                </button>
                <div className="flex items-start gap-1.5 text-slate-700">
                  <span className="shrink-0 text-[9.5px] font-bold uppercase text-slate-400">
                    Project Ref
                  </span>
                  <span
                    className="min-w-0 break-words font-mono text-[10.5px] font-bold text-slate-900"
                    data-testid={`grouped-project-reference-${proj.id}`}
                  >
                    {proj.caNumber || "—"}
                  </span>
                </div>
              </div>
            </td>
          );
        }

        return (
          <td
            key={`${proj.id}-partyName`}
            className={`${rowPadding} px-3 py-3 align-top border-r border-slate-200 text-slate-800`}
          >
            <div className="space-y-1.5 text-[11px] leading-snug">
              {/* 1. First Company Name, then Company Code */}
              <div className="flex items-start justify-between gap-1.5">
                <div className="flex items-center space-x-1.5 min-w-0 flex-wrap gap-y-1">
                  {/* Customer / Company Name */}
                  <div className="flex items-center space-x-1 min-w-0">
                    <Building className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span
                      className="font-extrabold text-slate-900 text-xs truncate max-w-[170px]"
                      title={party.companyName}
                    >
                      {party.companyName}
                    </span>
                  </div>

                  {/* Then: Company Code (DVEPL Code) */}
                  <button
                    type="button"
                    onClick={() => onOpenDetailSheet(proj)}
                    className="inline-flex items-center space-x-1 px-1.5 py-0.5 rounded font-mono font-black text-[10.5px] bg-emerald-100 text-emerald-900 border border-emerald-300 hover:bg-emerald-200 hover:border-emerald-500 hover:text-emerald-950 transition-all cursor-pointer shrink-0 shadow-2xs group"
                    title={`Click to open full Job Detail Sheet for DVEPL-${proj.dveplCode}`}
                  >
                    <span>DVEPL-{proj.dveplCode}</span>
                    <ExternalLink className="w-2.5 h-2.5 text-emerald-600 group-hover:text-emerald-800" />
                  </button>

                  {proj.priority === "Critical" && (
                    <span
                      className="w-2 h-2 rounded-full bg-rose-500 animate-pulse shrink-0"
                      title="Critical Priority"
                    />
                  )}
                </div>
                {groupByCompany && (
                  <span
                    className="w-1.5 h-1.5 rounded-full bg-emerald-600 shrink-0 mt-1"
                    title="Grouped company order"
                  />
                )}
              </div>

              {/* Below this: Project Reference :- */}
              <div className="flex items-center space-x-1 text-[11px] leading-tight text-amber-950">
                <span className="text-amber-800 font-bold shrink-0">
                  Project Reference :-
                </span>
                <span
                  className="font-mono font-extrabold text-slate-900 truncate"
                  title={proj.caNumber || "N/A"}
                >
                  {proj.caNumber || "—"}
                </span>
              </div>

              {/* Contact Details: Contact Person, Mobile no, email id */}
              <div className="space-y-1">
                {/* 2. Contact Person */}
                <div className="flex items-center justify-between text-slate-700 min-h-[16px]">
                  <span className="text-[10px] font-medium text-slate-500 flex items-center space-x-1">
                    <User className="w-3 h-3 text-emerald-500 shrink-0" />
                    <span>Contact Person:</span>
                  </span>
                  <span
                    className="font-bold text-slate-800 text-[10.5px] truncate max-w-[155px]"
                    title={party.contactPerson || ""}
                  >
                    {party.contactPerson || ""}
                  </span>
                </div>

                {/* 3. Mobile no - leave blank if not available */}
                <div className="flex items-center justify-between text-slate-700 min-h-[16px]">
                  <span className="text-[10px] font-medium text-slate-500 flex items-center space-x-1">
                    <Phone className="w-3 h-3 text-emerald-600 shrink-0" />
                    <span>Mobile no:</span>
                  </span>
                  {party.mobileNo ? (
                    <a
                      href={`tel:${party.mobileNo.replace(/[^0-9+]/g, "")}`}
                      onClick={(e) => e.stopPropagation()}
                      className="font-mono font-semibold text-emerald-700 hover:text-emerald-900 hover:underline text-[10.5px]"
                      title={`Call ${party.mobileNo}`}
                    >
                      {party.mobileNo}
                    </a>
                  ) : (
                    <span className="font-mono text-slate-400 text-[10.5px]"></span>
                  )}
                </div>

                {/* 4. email id - leave blank if not available */}
                <div className="flex items-center justify-between text-slate-700 min-h-[16px]">
                  <span className="text-[10px] font-medium text-slate-500 flex items-center space-x-1">
                    <Mail className="w-3 h-3 text-purple-500 shrink-0" />
                    <span>email id:</span>
                  </span>
                  {party.emailId ? (
                    <a
                      href={`mailto:${party.emailId}`}
                      onClick={(e) => e.stopPropagation()}
                      className="font-mono text-[10px] text-purple-700 hover:text-purple-900 hover:underline truncate max-w-[150px]"
                      title={`Email ${party.emailId}`}
                    >
                      {party.emailId}
                    </a>
                  ) : (
                    <span className="font-mono text-slate-400 text-[10.5px]"></span>
                  )}
                </div>
              </div>
            </div>
          </td>
        );
      }

      case "itemDescription": {
        const summary = getProjectDetailsSummary(proj);
        return (
          <td
            key={`${proj.id}-itemDescription`}
            className={`${rowPadding} px-2.5 border-r border-slate-200 text-slate-700 min-w-[240px] max-w-[300px]`}
          >
            <button
              type="button"
              onClick={() => onOpenDetailSheet(proj)}
              className="text-left w-full block hover:opacity-95 transition-all group cursor-pointer focus:outline-none"
              title={`Click to open full Project Details sheet for DVEPL-${proj.dveplCode}`}
            >
              {/* 6 Structured Rows matching User Image */}
              <div className="bg-white rounded border border-slate-200 divide-y divide-slate-100 text-[11px] leading-tight overflow-hidden shadow-2xs group-hover:border-emerald-300">
                {/* 1. Total Items */}
                <div className="flex items-center justify-between px-2.5 py-1 group-hover:bg-slate-50/70">
                  <span className="text-slate-700 font-medium">
                    Total Items
                  </span>
                  <span className="font-mono font-bold text-slate-900">
                    {summary.totalItems ? summary.totalItems : ""}
                  </span>
                </div>

                {/* 2. Total Qty */}
                <div className="flex items-center justify-between px-2.5 py-1 group-hover:bg-slate-50/70">
                  <span className="text-slate-700 font-medium">Total Qty</span>
                  <span className="font-mono font-bold text-slate-900">
                    {summary.totalQty ? `${summary.totalQty} Nos` : ""}
                  </span>
                </div>

                {/* 3. Total Amount with GST */}
                <div className="flex items-center justify-between px-2.5 py-1 group-hover:bg-slate-50/70">
                  <span className="text-slate-700 font-medium">
                    Total Amount with GST
                  </span>
                  <span className="font-mono font-bold text-emerald-700">
                    {summary.totalAmountWithGst
                      ? `₹${summary.totalAmountWithGst.toLocaleString("en-IN")}`
                      : ""}
                  </span>
                </div>

                {/* 4. Total Amount without GST */}
                <div className="flex items-center justify-between px-2.5 py-1 group-hover:bg-slate-50/70">
                  <span className="text-slate-700 font-medium">
                    Total Amount without GST
                  </span>
                  <span className="font-mono font-semibold text-slate-700">
                    {summary.totalAmountWithoutGst
                      ? `₹${summary.totalAmountWithoutGst.toLocaleString("en-IN")}`
                      : ""}
                  </span>
                </div>

                {/* 5. Advance */}
                <div className="flex items-center justify-between px-2.5 py-1 group-hover:bg-slate-50/70">
                  <span className="text-slate-700 font-medium">Advance</span>
                  <span className="font-mono font-semibold text-teal-700">
                    {summary.advance
                      ? `₹${summary.advance.toLocaleString("en-IN")}`
                      : ""}
                  </span>
                </div>

                {/* 6. Balance */}
                <div className="flex items-center justify-between px-2.5 py-1 group-hover:bg-slate-50/70">
                  <span className="text-slate-700 font-medium">Balance</span>
                  <span
                    className={`font-mono font-bold ${summary.balance ? "text-amber-700" : "text-slate-800"}`}
                  >
                    {summary.balance
                      ? `₹${summary.balance.toLocaleString("en-IN")}`
                      : ""}
                  </span>
                </div>
              </div>
            </button>
          </td>
        );
      }

      case "drawingSendDate":
        return (
          <td
            key={`${proj.id}-drawingSendDate`}
            className={`${rowPadding} px-3 border-r border-slate-200 text-slate-700 whitespace-nowrap font-mono`}
          >
            {proj.drawingSendDate ? (
              <span className="text-emerald-700 font-medium">
                {formatToDDMMYY(proj.drawingSendDate)}
              </span>
            ) : (
              <span className="text-slate-400 italic">Not Sent</span>
            )}
          </td>
        );

      case "drawingApprovedDate": {
        const commitment = getDeliveryCommitment(
          proj.deliveryTarget,
          proj.drawingApprovedDate,
          proj.drawingSendDate,
        );
        return (
          <td
            key={`${proj.id}-drawingApprovedDate`}
            className={`${rowPadding} px-3 border-r border-slate-200 text-slate-700 whitespace-nowrap font-mono`}
          >
            {proj.drawingApprovedDate ? (
              <div>
                <div className="flex items-center space-x-1 text-emerald-700 font-bold">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  <span>{formatToDDMMYY(proj.drawingApprovedDate)}</span>
                </div>
                {commitment.isCalculated ? (
                  <div
                    className="text-[9.5px] text-emerald-800 font-sans font-semibold mt-0.5"
                    title={commitment.subLabel}
                  >
                    Del: {commitment.displayDate}
                  </div>
                ) : proj.deliveryTarget ? (
                  <div
                    className="text-[9.5px] text-slate-500 font-sans mt-0.5"
                    title="Delivery commitment active"
                  >
                    Del: {formatToDDMMYY(proj.deliveryTarget)}
                  </div>
                ) : null}
              </div>
            ) : (
              <span className="text-amber-700 font-semibold text-xs">
                Pending Approval
              </span>
            )}
          </td>
        );
      }

      case "poNumber":
      case "vendorDetails": {
        const pos = getProjectPOs(proj);
        const totals = calculateProjectPOsTotal(pos);
        const activeTabKey =
          activePoTabByProject[proj.id] ?? (pos.length > 1 ? "all" : 0);

        return (
          <td
            key={`${proj.id}-vendorDetails`}
            className={`${rowPadding} px-2.5 border-r border-slate-200 text-slate-700 min-w-[280px] max-w-[340px]`}
          >
            {pos.length === 0 ? (
              <div className="bg-white rounded border border-dashed border-slate-300 p-3 text-center space-y-1.5 shadow-2xs hover:border-emerald-400 transition-colors">
                <div className="text-[11px] font-semibold text-slate-500">
                  No PO / Vendor Assigned
                </div>
                <button
                  type="button"
                  onClick={() =>
                    onManagePOs ? onManagePOs(proj) : onOpenEditModal(proj)
                  }
                  className="inline-flex items-center space-x-1 px-2.5 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-[11px] font-bold rounded border border-emerald-200 transition-colors cursor-pointer"
                >
                  <Plus className="w-3 h-3" />
                  <span>+ Add Vendor PO</span>
                </button>
              </div>
            ) : pos.length === 1 ? (
              /* Single PO: Exact 7 Structured Rows matching User Image + Add PO Action */
              <div className="bg-white rounded border border-slate-200 divide-y divide-slate-100 text-[11px] leading-tight overflow-hidden shadow-2xs hover:border-emerald-300 transition-colors">
                {/* Header with PO number & quick Add PO button */}
                <div className="bg-slate-50/80 px-2.5 py-1 flex items-center justify-between border-b border-slate-200 text-[10px]">
                  <div className="flex items-center space-x-1.5">
                    <span className="font-mono font-bold text-emerald-700">
                      PO #{pos[0].poNumber || "-"}
                    </span>
                    {pos[0].category && (
                      <span className="text-[9.5px] px-1 py-0.2 rounded bg-slate-200 text-slate-700 truncate max-w-[120px]">
                        {pos[0].category}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      onManagePOs ? onManagePOs(proj) : onOpenEditModal(proj)
                    }
                    className="text-emerald-600 hover:text-emerald-900 font-bold hover:underline flex items-center space-x-0.5 cursor-pointer"
                    title="Add another PO / Vendor for this Order"
                  >
                    <Plus className="w-3 h-3" />
                    <span>Add PO</span>
                  </button>
                </div>

                {/* 1. Vender Name */}
                <div className="flex items-center justify-between px-2.5 py-1 hover:bg-slate-50/70">
                  <span className="text-slate-700 font-medium">
                    Vender Name
                  </span>
                  <span
                    className="font-semibold text-slate-900 truncate max-w-[160px]"
                    title={pos[0].vendorName}
                  >
                    {pos[0].vendorName || "-"}
                  </span>
                </div>

                {/* 2. Concrned Person */}
                <div className="flex items-center justify-between px-2.5 py-1 hover:bg-slate-50/70">
                  <span className="text-slate-700 font-medium">
                    Concrned Person
                  </span>
                  <span
                    className="font-medium text-slate-800 truncate max-w-[160px]"
                    title={pos[0].concernedPerson || "-"}
                  >
                    {pos[0].concernedPerson || "-"}
                  </span>
                </div>

                {/* 3. Mobile No */}
                <div className="flex items-center justify-between px-2.5 py-1 hover:bg-slate-50/70">
                  <span className="text-slate-700 font-medium">Mobile No</span>
                  {pos[0].mobileNo ? (
                    <a
                      href={`tel:${pos[0].mobileNo.replace(/[^0-9+]/g, "")}`}
                      className="font-mono font-semibold text-emerald-700 hover:underline"
                    >
                      {pos[0].mobileNo}
                    </a>
                  ) : (
                    <span className="font-mono text-slate-400">-</span>
                  )}
                </div>

                {/* 4. Email id */}
                <div className="flex items-center justify-between px-2.5 py-1 hover:bg-slate-50/70">
                  <span className="text-slate-700 font-medium">Email id</span>
                  {pos[0].emailId ? (
                    <a
                      href={`mailto:${pos[0].emailId}`}
                      className="text-purple-700 truncate max-w-[160px] hover:underline"
                      title={pos[0].emailId}
                    >
                      {pos[0].emailId}
                    </a>
                  ) : (
                    <span className="text-slate-400">-</span>
                  )}
                </div>

                {/* 5. PO Number / Dt */}
                <div className="flex items-center justify-between px-2.5 py-1 hover:bg-slate-50/70">
                  <span className="text-slate-700 font-medium">
                    PO Number / Dt
                  </span>
                  <span className="font-mono font-semibold text-slate-900">
                    {pos[0].poNumber
                      ? `${pos[0].poNumber}${pos[0].poDate ? ` / ${formatToDDMMYY(pos[0].poDate)}` : ""}`
                      : "-"}
                  </span>
                </div>

                {/* 6. Amount without Gst */}
                <div className="flex items-center justify-between px-2.5 py-1 hover:bg-slate-50/70">
                  <span className="text-slate-700 font-medium">
                    Amount without Gst
                  </span>
                  <span className="font-mono font-medium text-slate-700">
                    {pos[0].amountWithoutGst
                      ? `₹${pos[0].amountWithoutGst.toLocaleString("en-IN")}`
                      : "-"}
                  </span>
                </div>

                {/* 7. With GST */}
                <div className="flex items-center justify-between px-2.5 py-1 hover:bg-slate-50/70">
                  <span className="text-slate-700 font-medium">With GST</span>
                  <span className="font-mono font-bold text-emerald-700">
                    {pos[0].amountWithGst
                      ? `₹${pos[0].amountWithGst.toLocaleString("en-IN")}`
                      : "-"}
                  </span>
                </div>

                {/* Material Status Note if present */}
                {pos[0].materialStatus && (
                  <div className="px-2.5 py-1 bg-amber-50/70 text-[10px] text-amber-900 border-t border-amber-100 flex items-start space-x-1">
                    <span className="font-bold shrink-0">Status:</span>
                    <span className="truncate" title={pos[0].materialStatus}>
                      {pos[0].materialStatus}
                    </span>
                  </div>
                )}

                {/* Store Inward / Excel BOM Status & Quick Trigger */}
                {(() => {
                  const po0 = pos[0];
                  const hasItems = po0.items && po0.items.length > 0;
                  const itemStats = calculatePOItemStats(po0.items);
                  return (
                    <div className="px-2.5 py-1 bg-slate-50 border-t border-slate-100 flex items-center justify-between text-[10px]">
                      {hasItems ? (
                        <div
                          className="flex items-center space-x-1 text-slate-700 truncate max-w-[170px]"
                          title={`${po0.uploadedFileName || "Excel BOM"}: ${po0.items?.length} items`}
                        >
                          <FileSpreadsheet className="w-3 h-3 text-emerald-600 shrink-0" />
                          <span className="font-semibold text-slate-800 truncate">
                            {po0.uploadedFileName || "Excel BOM"}
                          </span>
                          <span
                            className={
                              itemStats.percentDelivered === 100
                                ? "text-emerald-700 font-bold"
                                : "text-emerald-700 font-bold"
                            }
                          >
                            ({itemStats.totalReceivedQty}/
                            {itemStats.totalOrderedQty})
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-1 text-slate-500">
                          <PackageCheck className="w-3 h-3 text-slate-400 shrink-0" />
                          <span>Store & Follow-up:</span>
                        </div>
                      )}
                      {onOpenStoreDesk && (
                        <button
                          type="button"
                          onClick={() => onOpenStoreDesk(proj, po0.id)}
                          className="text-emerald-600 hover:text-emerald-900 font-bold hover:underline shrink-0 flex items-center space-x-0.5 cursor-pointer ml-1"
                          title="Open Store Inward Desk & Vendor Follow-ups"
                        >
                          <PackageCheck className="w-3 h-3 text-emerald-600" />
                          <span>
                            {hasItems ? "Store Desk" : "Upload Excel"}
                          </span>
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>
            ) : (
              /* Multiple POs: Segmented Tabs + Stacked / Single PO View */
              <div className="bg-white rounded border border-emerald-200 text-[11px] leading-tight overflow-hidden shadow-2xs hover:border-emerald-400 transition-colors">
                {/* Multi-PO Bar */}
                <div className="bg-emerald-50/90 px-2 py-1 border-b border-emerald-100 flex items-center justify-between">
                  <div className="flex items-center space-x-1.5">
                    <span className="px-1.5 py-0.5 rounded bg-emerald-600 text-white font-bold text-[9.5px]">
                      {pos.length} POs
                    </span>
                    <span className="text-[10px] font-bold text-emerald-950">
                      Multiple Vendors
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {onOpenStoreDesk && (
                      <button
                        type="button"
                        onClick={() => onOpenStoreDesk(proj)}
                        className="text-[10px] font-bold text-emerald-700 hover:text-emerald-900 hover:underline flex items-center space-x-0.5 cursor-pointer"
                        title="Open Store Inward & Follow-up Desk for this project"
                      >
                        <PackageCheck className="w-3 h-3 text-emerald-600" />
                        <span>Store Desk</span>
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() =>
                        onManagePOs ? onManagePOs(proj) : onOpenEditModal(proj)
                      }
                      className="text-[10px] font-bold text-emerald-700 hover:text-emerald-950 hover:underline flex items-center space-x-0.5 cursor-pointer"
                      title="Manage & Edit All POs for this Order"
                    >
                      <Edit className="w-3 h-3" />
                      <span>Manage</span>
                    </button>
                  </div>
                </div>

                {/* Tab selector */}
                <div className="flex items-center bg-slate-100 p-0.5 border-b border-slate-200 overflow-x-auto scrollbar-none gap-0.5 text-[10px]">
                  <button
                    type="button"
                    onClick={() =>
                      setActivePoTabByProject((prev) => ({
                        ...prev,
                        [proj.id]: "all",
                      }))
                    }
                    className={`px-2 py-0.5 rounded font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                      activeTabKey === "all"
                        ? "bg-white text-emerald-800 shadow-2xs font-bold"
                        : "text-slate-600 hover:text-slate-900 hover:bg-slate-200/60"
                    }`}
                  >
                    Stacked All ({pos.length})
                  </button>
                  {pos.map((po, idx) => (
                    <button
                      key={po.id || idx}
                      type="button"
                      onClick={() =>
                        setActivePoTabByProject((prev) => ({
                          ...prev,
                          [proj.id]: idx,
                        }))
                      }
                      className={`px-2 py-0.5 rounded font-semibold whitespace-nowrap transition-colors truncate max-w-[90px] cursor-pointer ${
                        activeTabKey === idx
                          ? "bg-white text-emerald-800 shadow-2xs font-bold"
                          : "text-slate-600 hover:text-slate-900 hover:bg-slate-200/60"
                      }`}
                      title={`${idx + 1}. ${po.vendorName || "PO"}`}
                    >
                      {idx + 1}.{" "}
                      {po.vendorName?.split(" ")[0] || `PO ${idx + 1}`}
                    </button>
                  ))}
                </div>

                {/* Content View: Stacked vs Specific Tab */}
                {activeTabKey === "all" ? (
                  <div className="divide-y divide-slate-100 max-h-[220px] overflow-y-auto">
                    {pos.map((po, idx) => (
                      <div
                        key={po.id || idx}
                        className="p-2 space-y-1 hover:bg-slate-50/70 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-1 font-bold text-slate-900">
                            <span className="w-3.5 h-3.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] flex items-center justify-center font-bold">
                              {idx + 1}
                            </span>
                            <span
                              className="truncate max-w-[130px]"
                              title={po.vendorName}
                            >
                              {po.vendorName}
                            </span>
                          </div>
                          <span className="font-mono font-bold text-emerald-700 text-[10px] bg-emerald-50 px-1 py-0.2 rounded border border-emerald-100">
                            #{po.poNumber || "-"}
                          </span>
                        </div>

                        <div className="flex items-center justify-between text-[10px] text-slate-600">
                          <span className="truncate max-w-[130px]">
                            {po.concernedPerson || "Contact"}
                          </span>
                          {po.mobileNo && (
                            <a
                              href={`tel:${po.mobileNo}`}
                              className="font-mono text-emerald-700 hover:underline"
                            >
                              {po.mobileNo}
                            </a>
                          )}
                        </div>

                        <div className="flex items-center justify-between text-[10px] pt-0.5 border-t border-slate-100/60">
                          <span className="text-slate-500">
                            w/o GST:{" "}
                            <strong className="text-slate-800 font-mono">
                              ₹
                              {po.amountWithoutGst?.toLocaleString("en-IN") ||
                                "-"}
                            </strong>
                          </span>
                          <span className="text-emerald-700 font-bold font-mono">
                            ₹{po.amountWithGst?.toLocaleString("en-IN") || "-"}
                          </span>
                        </div>

                        {po.materialStatus && (
                          <div
                            className="text-[9.5px] text-amber-800 bg-amber-50/60 px-1.5 py-0.5 rounded truncate"
                            title={po.materialStatus}
                          >
                            {po.materialStatus}
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Overall Summary Bar */}
                    <div className="bg-slate-50 px-2 py-1.5 border-t border-slate-200 flex items-center justify-between text-[10px]">
                      <span className="font-bold text-slate-700">
                        Total ({pos.length} POs):
                      </span>
                      <div className="space-x-2 font-mono">
                        <span className="text-slate-600">
                          w/o:{" "}
                          <strong>
                            ₹{totals.totalWithoutGst.toLocaleString("en-IN")}
                          </strong>
                        </span>
                        <span className="text-emerald-700 font-bold">
                          w/GST: ₹{totals.totalWithGst.toLocaleString("en-IN")}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Single Tab View within Multiple POs: Exact 7 Rows for selected PO */
                  (() => {
                    const selIdx =
                      typeof activeTabKey === "number" ? activeTabKey : 0;
                    const curPo = pos[selIdx] || pos[0];
                    return (
                      <div className="divide-y divide-slate-100 text-[11px] leading-tight">
                        {/* 1. Vender Name */}
                        <div className="flex items-center justify-between px-2.5 py-1 hover:bg-slate-50/70">
                          <span className="text-slate-700 font-medium">
                            Vender Name
                          </span>
                          <span
                            className="font-semibold text-slate-900 truncate max-w-[160px]"
                            title={curPo.vendorName}
                          >
                            {curPo.vendorName || "-"}
                          </span>
                        </div>

                        {/* 2. Concrned Person */}
                        <div className="flex items-center justify-between px-2.5 py-1 hover:bg-slate-50/70">
                          <span className="text-slate-700 font-medium">
                            Concrned Person
                          </span>
                          <span
                            className="font-medium text-slate-800 truncate max-w-[160px]"
                            title={curPo.concernedPerson || "-"}
                          >
                            {curPo.concernedPerson || "-"}
                          </span>
                        </div>

                        {/* 3. Mobile No */}
                        <div className="flex items-center justify-between px-2.5 py-1 hover:bg-slate-50/70">
                          <span className="text-slate-700 font-medium">
                            Mobile No
                          </span>
                          {curPo.mobileNo ? (
                            <a
                              href={`tel:${curPo.mobileNo.replace(/[^0-9+]/g, "")}`}
                              className="font-mono font-semibold text-emerald-700 hover:underline"
                            >
                              {curPo.mobileNo}
                            </a>
                          ) : (
                            <span className="font-mono text-slate-400">-</span>
                          )}
                        </div>

                        {/* 4. Email id */}
                        <div className="flex items-center justify-between px-2.5 py-1 hover:bg-slate-50/70">
                          <span className="text-slate-700 font-medium">
                            Email id
                          </span>
                          {curPo.emailId ? (
                            <a
                              href={`mailto:${curPo.emailId}`}
                              className="text-purple-700 truncate max-w-[160px] hover:underline"
                              title={curPo.emailId}
                            >
                              {curPo.emailId}
                            </a>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </div>

                        {/* 5. PO Number / Dt */}
                        <div className="flex items-center justify-between px-2.5 py-1 hover:bg-slate-50/70">
                          <span className="text-slate-700 font-medium">
                            PO Number / Dt
                          </span>
                          <span className="font-mono font-semibold text-slate-900">
                            {curPo.poNumber
                              ? `${curPo.poNumber}${curPo.poDate ? ` / ${formatToDDMMYY(curPo.poDate)}` : ""}`
                              : "-"}
                          </span>
                        </div>

                        {/* 6. Amount without Gst */}
                        <div className="flex items-center justify-between px-2.5 py-1 hover:bg-slate-50/70">
                          <span className="text-slate-700 font-medium">
                            Amount without Gst
                          </span>
                          <span className="font-mono font-medium text-slate-700">
                            {curPo.amountWithoutGst
                              ? `₹${curPo.amountWithoutGst.toLocaleString("en-IN")}`
                              : "-"}
                          </span>
                        </div>

                        {/* 7. With GST */}
                        <div className="flex items-center justify-between px-2.5 py-1 hover:bg-slate-50/70">
                          <span className="text-slate-700 font-medium">
                            With GST
                          </span>
                          <span className="font-mono font-bold text-emerald-700">
                            {curPo.amountWithGst
                              ? `₹${curPo.amountWithGst.toLocaleString("en-IN")}`
                              : "-"}
                          </span>
                        </div>

                        {/* Store Inward / Excel BOM Status & Trigger for this PO */}
                        {(() => {
                          const hasItems =
                            curPo.items && curPo.items.length > 0;
                          const itemStats = calculatePOItemStats(curPo.items);
                          return (
                            <div className="px-2.5 py-1 bg-slate-50 border-t border-slate-100 flex items-center justify-between text-[10px]">
                              {hasItems ? (
                                <div
                                  className="flex items-center space-x-1 text-slate-700 truncate max-w-[160px]"
                                  title={`${curPo.uploadedFileName || "Excel BOM"}: ${curPo.items?.length} items`}
                                >
                                  <FileSpreadsheet className="w-3 h-3 text-emerald-600 shrink-0" />
                                  <span className="font-semibold text-slate-800 truncate">
                                    {curPo.uploadedFileName || "Excel BOM"}
                                  </span>
                                  <span
                                    className={
                                      itemStats.percentDelivered === 100
                                        ? "text-emerald-700 font-bold"
                                        : "text-emerald-700 font-bold"
                                    }
                                  >
                                    ({itemStats.totalReceivedQty}/
                                    {itemStats.totalOrderedQty})
                                  </span>
                                </div>
                              ) : (
                                <div className="flex items-center space-x-1 text-slate-500">
                                  <PackageCheck className="w-3 h-3 text-slate-400 shrink-0" />
                                  <span>Store & Inward:</span>
                                </div>
                              )}
                              {onOpenStoreDesk && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    onOpenStoreDesk(proj, curPo.id)
                                  }
                                  className="text-emerald-600 hover:text-emerald-900 font-bold hover:underline shrink-0 flex items-center space-x-0.5 cursor-pointer ml-1"
                                  title={`Open Store Desk for ${curPo.vendorName || "this PO"}`}
                                >
                                  <PackageCheck className="w-3 h-3 text-emerald-600" />
                                  <span>
                                    {hasItems ? "Store Desk" : "Upload Excel"}
                                  </span>
                                </button>
                              )}
                            </div>
                          );
                        })()}

                        {/* Note & Overall totals */}
                        <div className="bg-slate-50 px-2 py-1 flex items-center justify-between text-[9.5px] text-slate-500 border-t border-slate-200">
                          <span>
                            PO {selIdx + 1} of {pos.length}
                          </span>
                          <span className="font-mono font-semibold text-emerald-700">
                            Combined: ₹
                            {totals.totalWithGst.toLocaleString("en-IN")}
                          </span>
                        </div>
                      </div>
                    );
                  })()
                )}
              </div>
            )}
          </td>
        );
      }

      case "stage": {
        const stg = getProjectStageStatus(proj);
        return (
          <td
            key={`${proj.id}-stage`}
            className={`${rowPadding} px-2 border-r border-slate-200 text-xs min-w-[280px] max-w-[340px]`}
          >
            <div className="bg-white rounded border border-slate-200 divide-y divide-slate-100 text-[11px] leading-tight overflow-hidden shadow-2xs">
              {/* Header row: Head | SubHead */}
              <div className="flex items-center justify-between px-2.5 py-1 bg-slate-50 text-[10px] font-extrabold text-slate-600 uppercase tracking-wider border-b border-slate-200">
                <span className="flex items-center space-x-1">
                  <Layers className="w-3 h-3 text-slate-500" />
                  <span>Head</span>
                </span>
                <span>SubHead</span>
              </div>

              {/* 1. Drawings: Send , Approved */}
              <div className="flex items-center justify-between px-2.5 py-1 hover:bg-slate-50/70">
                <div className="flex items-center space-x-1.5 min-w-[85px]">
                  <span className="w-1.5 h-1.5 rounded-full bg-sky-500 shrink-0"></span>
                  <span className="font-semibold text-slate-800">Drawings</span>
                </div>
                <select
                  value={stg.drawings}
                  onChange={(e) =>
                    handleUpdateStageSubHead(
                      proj.id,
                      "Drawings",
                      e.target.value,
                    )
                  }
                  className={`text-[10.5px] font-bold px-2 py-0.5 rounded border focus:outline-none cursor-pointer transition-colors ${
                    stg.drawings === "Approved"
                      ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                      : stg.drawings === "Send"
                        ? "bg-sky-50 text-sky-800 border-sky-300"
                        : "bg-slate-50 text-slate-500 border-slate-200"
                  }`}
                  title="Drawings SubHead: Send, Approved"
                >
                  <option value="">-</option>
                  <option value="Send">Send</option>
                  <option value="Approved">Approved</option>
                </select>
              </div>

              {/* 2. PO: Ready, Placed */}
              <div className="flex items-center justify-between px-2.5 py-1 hover:bg-slate-50/70">
                <div className="flex items-center space-x-1.5 min-w-[85px]">
                  <span className="w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0"></span>
                  <span className="font-semibold text-slate-800">PO</span>
                </div>
                <select
                  value={stg.po}
                  onChange={(e) =>
                    handleUpdateStageSubHead(proj.id, "PO", e.target.value)
                  }
                  className={`text-[10.5px] font-bold px-2 py-0.5 rounded border focus:outline-none cursor-pointer transition-colors ${
                    stg.po === "Placed"
                      ? "bg-purple-50 text-purple-800 border-purple-300"
                      : stg.po === "Ready"
                        ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                        : "bg-slate-50 text-slate-500 border-slate-200"
                  }`}
                  title="PO SubHead: Ready, Placed"
                >
                  <option value="">-</option>
                  <option value="Ready">Ready</option>
                  <option value="Placed">Placed</option>
                </select>
              </div>

              {/* 3. Material: Not Recevied , Partially Recevied, Fully recevied */}
              <div className="flex items-center justify-between px-2.5 py-1 hover:bg-slate-50/70">
                <div className="flex items-center space-x-1.5 min-w-[85px]">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0"></span>
                  <span className="font-semibold text-slate-800">Material</span>
                </div>
                <select
                  value={stg.material}
                  onChange={(e) =>
                    handleUpdateStageSubHead(
                      proj.id,
                      "Material",
                      e.target.value,
                    )
                  }
                  className={`text-[10.5px] font-bold px-2 py-0.5 rounded border focus:outline-none cursor-pointer transition-colors ${
                    stg.material === "Fully recevied"
                      ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                      : stg.material === "Partially Recevied"
                        ? "bg-amber-50 text-amber-800 border-amber-300"
                        : stg.material === "Not Recevied"
                          ? "bg-rose-50 text-rose-800 border-rose-300 font-extrabold"
                          : "bg-slate-50 text-slate-500 border-slate-200"
                  }`}
                  title="Material SubHead: Not Recevied, Partially Recevied, Fully recevied"
                >
                  <option value="">-</option>
                  <option value="Not Recevied">Not Recevied</option>
                  <option value="Partially Recevied">Partially Recevied</option>
                  <option value="Fully recevied">Fully recevied</option>
                </select>
              </div>

              {/* 4. Production: Cutter, Laser, welding. Powder coating , Assembly, Busbar section, Electrical , Testing , Ready */}
              <div className="flex items-center justify-between px-2.5 py-1 hover:bg-slate-50/70">
                <div className="flex items-center space-x-1.5 min-w-[85px]">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0"></span>
                  <span className="font-semibold text-slate-800">
                    Production
                  </span>
                </div>
                <select
                  value={stg.production}
                  onChange={(e) =>
                    handleUpdateStageSubHead(
                      proj.id,
                      "Production",
                      e.target.value,
                    )
                  }
                  className={`text-[10.5px] font-bold px-2 py-0.5 rounded border focus:outline-none cursor-pointer max-w-[170px] truncate transition-colors ${
                    stg.production === "Ready"
                      ? "bg-emerald-50 text-emerald-800 border-emerald-300 font-extrabold"
                      : stg.production === "Testing"
                        ? "bg-cyan-50 text-cyan-800 border-cyan-300"
                        : stg.production === "Electrical"
                          ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                          : stg.production === "Busbar section"
                            ? "bg-teal-50 text-teal-800 border-teal-300"
                            : stg.production === "Assembly"
                              ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                              : stg.production === "Powder coating"
                                ? "bg-purple-50 text-purple-800 border-purple-300"
                                : stg.production === "welding"
                                  ? "bg-amber-50 text-amber-800 border-amber-300"
                                  : stg.production === "Laser"
                                    ? "bg-violet-50 text-violet-800 border-violet-300"
                                    : stg.production === "Cutter"
                                      ? "bg-slate-100 text-slate-800 border-slate-300"
                                      : "bg-slate-50 text-slate-400 border-slate-200"
                  }`}
                  title="Production SubHead: Cutter, Laser, welding, Powder coating, Assembly, Busbar section, Electrical, Testing, Ready"
                >
                  <option value="">- Not Started -</option>
                  <option value="Cutter">Cutter</option>
                  <option value="Laser">Laser</option>
                  <option value="welding">welding</option>
                  <option value="Powder coating">Powder coating</option>
                  <option value="Assembly">Assembly</option>
                  <option value="Busbar section">Busbar section</option>
                  <option value="Electrical">Electrical</option>
                  <option value="Testing">Testing</option>
                  <option value="Ready">Ready</option>
                </select>
              </div>
            </div>
          </td>
        );
      }

      case "followUpCount":
        return (
          <td
            key={`${proj.id}-followUpCount`}
            className={`${rowPadding} px-3 border-r border-slate-200 text-center whitespace-nowrap`}
          >
            <span
              className={`px-2 py-0.5 text-[11px] font-bold rounded-full border ${
                (proj.followUpCount || 0) >= 9
                  ? "bg-rose-100 text-rose-800 border-rose-300"
                  : (proj.followUpCount || 0) >= 3
                    ? "bg-amber-100 text-amber-800 border-amber-300"
                    : (proj.followUpCount || 0) > 0
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-slate-100 text-slate-600 border-slate-200"
              }`}
            >
              Reminder #{proj.followUpCount || 0}
            </span>
            <span className="text-[10px] text-slate-400 block mt-0.5">
              every {proj.followUpCadenceDays || 3}d
            </span>
          </td>
        );

      case "billNumber":
        return (
          <td
            key={`${proj.id}-billNumber`}
            className={`${rowPadding} px-3 border-r border-slate-200 text-slate-700 whitespace-nowrap font-mono`}
          >
            {proj.billNumber ? (
              <div>
                <span className="font-semibold text-emerald-800">
                  {proj.billNumber}
                </span>
                {proj.dispatchDate && (
                  <span className="text-[10px] text-slate-500 block">
                    Date: {proj.dispatchDate}
                  </span>
                )}
              </div>
            ) : (
              <span className="text-slate-400 italic">Pending</span>
            )}
          </td>
        );

      default:
        return (
          <td
            key={`${proj.id}-${colId}`}
            className={`${rowPadding} px-3 border-r border-slate-200 text-slate-600 whitespace-nowrap`}
          >
            -
          </td>
        );
    }
  };

  const visibleColumnIds = columnOrder.filter(
    (c) => visibleColumns[c],
  );
  const visibleColumnsCount = visibleColumnIds.length;
  const groupLabelColumnId = visibleColumnIds.includes("partyName")
    ? "partyName"
    : visibleColumnIds.find((colId) => colId !== "slNo") || "slNo";

  const renderCompactDocuments = (proj: ProjectTask) => {
    const documents = proj.documents || [];

    return (
      <section
        className="border-t border-slate-200 pt-2"
        data-testid={`compact-project-documents-${proj.id}`}
      >
        <div className="flex items-center justify-between gap-2">
          <span
            className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase
              tracking-wide text-slate-700"
          >
            <FileText className="h-3.5 w-3.5 text-emerald-600" />
            Documents & Uploads
          </span>
          <span
            className="font-mono text-[10px] font-bold text-emerald-800"
            data-testid={`compact-project-documents-count-${proj.id}`}
          >
            {documents.length} file{documents.length === 1 ? "" : "s"}
          </span>
        </div>
        {documents.length === 0 ? (
          <p
            className="mt-1.5 text-[11px] text-slate-500"
            data-testid={`compact-project-documents-empty-${proj.id}`}
          >
            No uploaded files are linked to this job.
          </p>
        ) : (
          <div className="mt-2 space-y-2">
            {documents.map((document) => {
              const status = document.status || "Pending review";
              const statusClass =
                status === "Approved"
                  ? "text-emerald-800"
                  : status === "Rejected"
                    ? "text-rose-700"
                    : "text-amber-800";

              return (
                <div
                  key={document.id}
                  className="border border-slate-200 bg-white p-2"
                  data-testid={
                    `compact-project-document-row-${proj.id}-${document.id}`
                  }
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div
                        className="text-[9px] font-bold uppercase tracking-wide text-slate-500"
                        data-testid={
                          `compact-project-document-folder-${proj.id}-${document.id}`
                        }
                      >
                        {document.folder || "General"}
                      </div>
                      <div
                        className="mt-0.5 break-words text-[11px] font-bold text-slate-900"
                        data-testid={
                          `compact-project-document-name-${proj.id}-${document.id}`
                        }
                      >
                        {document.name}
                      </div>
                    </div>
                    <span
                      className={`shrink-0 text-[10px] font-semibold ${statusClass}`}
                      data-testid={
                        `compact-project-document-status-${proj.id}-${document.id}`
                      }
                    >
                      {status}
                    </span>
                  </div>
                  <div
                    className="mt-1 font-mono text-[10px] text-slate-600"
                    data-testid={
                      `compact-project-document-uploaded-at-${proj.id}-${document.id}`
                    }
                  >
                    Uploaded {formatToDDMMYY(document.uploadedAt) || "—"}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => onOpenDocument?.(proj, document)}
                      className="inline-flex items-center gap-1 border border-emerald-200
                        bg-emerald-50 px-2 py-1 text-[10px] font-bold text-emerald-800
                        hover:bg-emerald-100"
                      data-testid={
                        `compact-project-document-open-${proj.id}-${document.id}`
                      }
                    >
                      <Eye className="h-3 w-3" />
                      Open
                    </button>
                    <button
                      type="button"
                      onClick={() => onDownloadDocument?.(proj, document)}
                      className="inline-flex items-center gap-1 border border-slate-200 bg-white
                        px-2 py-1 text-[10px] font-bold text-slate-700 hover:bg-slate-50"
                      data-testid={
                        `compact-project-document-download-${proj.id}-${document.id}`
                      }
                    >
                      <Download className="h-3 w-3" />
                      Download
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    );
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
      <header
        className="bg-white px-4 py-4"
        data-testid="project-table-merged-header"
      >
        {headerSummary && (
          <div
            className="border-b border-slate-200 pb-4"
            data-testid="project-table-kpi-summary"
          >
            {headerSummary}
          </div>
        )}
        <div
          className={`flex flex-wrap items-center gap-2 ${
            headerSummary ? "pt-4" : ""
          }`}
        >
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search job code, customer, PO, or item..."
            className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-xs text-slate-800 outline-none transition-colors focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
            data-testid="project-table-search"
          />
        </div>
        <div className="relative hidden">
          <button
            type="button"
            onClick={() => setFiltersOpen((open) => !open)}
            data-testid="project-table-filters-toggle"
            className={`inline-flex items-center rounded-lg border px-3 py-2 text-xs font-bold transition-colors ${filtersOpen ? "border-emerald-600 bg-emerald-600 text-white" : "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"}`}
          >
            <SlidersHorizontal className="mr-1.5 h-3.5 w-3.5" /> Filters
          </button>
          {filtersOpen && (
            <div className="absolute right-0 top-full z-50 mt-2 grid w-[min(92vw,680px)] grid-cols-1 gap-2 rounded-xl border border-emerald-200 bg-white p-3 shadow-2xl sm:grid-cols-2">
              <select
                value={selectedEngineer}
                onChange={(e) => setSelectedEngineer(e.target.value)}
                data-testid="project-table-engineer-filter"
                className="rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-700 outline-none focus:border-emerald-500"
              >
                <option value="all">Concerned Engineer: All</option>
                {engineers.map((engineer) => (
                  <option key={engineer.name} value={engineer.name}>
                    {engineer.name}
                    {engineer.role ? ` (${engineer.role})` : ""}
                  </option>
                ))}
              </select>
              <select
                value={selectedDrawingStatus}
                onChange={(e) =>
                  setSelectedDrawingStatus(e.target.value as any)
                }
                data-testid="project-table-drawing-filter"
                className="rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-700 outline-none focus:border-emerald-500"
              >
                <option value="all">Drawing Tasks: All</option>
                <option value="on_time">Drawings On Time</option>
                <option value="due_today">Drawings Due Today</option>
                <option value="overdue">Drawings Overdue</option>
                <option value="completed">Drawings Completed</option>
              </select>
              <select
                value={selectedStage}
                onChange={(e) => setSelectedStage(e.target.value)}
                data-testid="project-table-stage-filter"
                className="rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-700 outline-none focus:border-emerald-500"
              >
                <option value="all">Stage / Status: All</option>
                <option value="head:Drawings">Drawings</option>
                <option value="head:PO">PO</option>
                <option value="head:Material">Material</option>
                <option value="head:Production">Production</option>
              </select>
              <select
                value={selectedPriority}
                onChange={(e) => setSelectedPriority(e.target.value)}
                data-testid="project-table-priority-filter"
                className="rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-700 outline-none focus:border-emerald-500"
              >
                <option value="all">Priority: All</option>
                <option value="Critical">Critical</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
          )}
        </div>
        {onNewJob && (
          <button
            type="button"
            id="btn-create-new-job"
            data-testid="btn-create-new-job"
            onClick={() => onNewJob()}
            className="inline-flex items-center rounded-lg border border-emerald-700 bg-emerald-600 px-3 py-2 text-xs font-bold text-white hover:bg-emerald-700"
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            New Job
          </button>
        )}
        <button
          type="button"
          onClick={handleExpandAll}
          data-testid="project-table-expand-all"
          className="inline-flex items-center rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-800 transition-colors hover:border-emerald-400 hover:bg-emerald-100"
          title="Expand all customer groups"
        >
          Expand All
        </button>
        <button
          type="button"
          onClick={handleCollapseAll}
          data-testid="project-table-collapse-all"
          className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 transition-colors hover:border-emerald-300 hover:bg-emerald-50"
          title="Collapse all customer groups"
        >
          Collapse All
        </button>
        {onExportCsv && (
          <button
            type="button"
            id="btn-table-export-csv"
            onClick={onExportCsv}
            className="hidden items-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:border-emerald-300 hover:bg-emerald-50"
          >
            <Download className="mr-1.5 h-3.5 w-3.5 text-emerald-700" />
            Export
          </button>
        )}
        </div>
      </header>
      {/* Top Filter and Search Bar */}
      <div className="hidden p-4 border-b border-slate-200 space-y-3 bg-slate-50/50">
        {/* Navigation Tabs and View Switcher */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-200 pb-2">
          <div className="flex items-center space-x-1 overflow-x-auto pb-1">
            <button
              onClick={() => setActiveTab("all")}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg whitespace-nowrap transition-colors ${
                activeTab === "all"
                  ? "bg-emerald-600 text-white shadow-2xs"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              }`}
            >
              All Projects ({projects.length})
            </button>
            <button
              onClick={() => setActiveTab("drawing_pending")}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg whitespace-nowrap transition-colors ${
                activeTab === "drawing_pending"
                  ? "bg-amber-600 text-white shadow-2xs"
                  : "text-slate-600 hover:text-amber-800 hover:bg-amber-50"
              }`}
            >
              Drawing Approval Pending
            </button>
            <button
              onClick={() => setActiveTab("due_today")}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg whitespace-nowrap transition-colors ${
                activeTab === "due_today"
                  ? "bg-orange-600 text-white shadow-2xs"
                  : "text-slate-600 hover:text-orange-800 hover:bg-orange-50"
              }`}
            >
              Reminders Due Today
            </button>
            <button
              onClick={() => setActiveTab("material_pending")}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg whitespace-nowrap transition-colors ${
                activeTab === "material_pending"
                  ? "bg-rose-600 text-white shadow-2xs"
                  : "text-slate-600 hover:text-rose-800 hover:bg-rose-50"
              }`}
            >
              Vendor Material Delays
            </button>
            <button
              onClick={() => setActiveTab("in_production")}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg whitespace-nowrap transition-colors ${
                activeTab === "in_production"
                  ? "bg-emerald-600 text-white shadow-2xs"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              }`}
            >
              Shopfloor In-Process
            </button>
          </div>

          {/* Right Tools: View Mode Switcher, Column Selector & Density */}
          <div className="flex items-center space-x-2 shrink-0">
            {onNewJob && (
              <button
                type="button"
                id="btn-create-new-job"
                data-testid="legacy-btn-create-new-job"
                onClick={() => onNewJob()}
                className="inline-flex items-center px-3 py-1.5 text-xs font-bold rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 border border-emerald-700 shadow-2xs transition-all cursor-pointer"
                title="Create and allot a new job"
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                <span>New Job</span>
              </button>
            )}
            {/* Export CSV Button */}
            {onExportCsv && (
              <button
                type="button"
                id="btn-table-export-csv"
                onClick={onExportCsv}
                className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-lg text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 shadow-2xs transition-all cursor-pointer"
                title="Export tracking sheet to CSV"
              >
                <Download className="w-3.5 h-3.5 mr-1.5 text-slate-600" />
                <span>Export CSV</span>
              </button>
            )}

            {/* Drawing Tasks & Deadlines (Target Dates, On-Time, Overdue, Completed) */}
            <button
              type="button"
              id="btn-table-drawing-deadlines"
              onClick={() => setShowTeamDeadlinesModal(true)}
              className="inline-flex items-center px-3 py-1.5 text-xs font-bold rounded-lg text-amber-900 bg-amber-50 hover:bg-amber-100 border border-amber-300 shadow-2xs transition-all cursor-pointer"
              title="View which team member has which drawing job, target dates, and if on time, completed, or overdue"
            >
              <Clock className="w-3.5 h-3.5 mr-1.5 text-amber-600" />
              <span>Drawing Tasks &amp; Deadlines</span>
              {drawingStats.overdue > 0 ? (
                <span className="ml-1.5 px-1.5 py-0.2 bg-rose-600 text-white rounded-full text-[10px] font-extrabold animate-pulse">
                  {drawingStats.overdue} Overdue
                </span>
              ) : (
                <span className="ml-1.5 px-1.5 py-0.2 bg-emerald-100 text-emerald-800 rounded-full text-[10px] font-bold">
                  {drawingStats.completed}/{projects.length} Done
                </span>
              )}
            </button>

            {/* Team Profiles & Incharges Button */}
            {onOpenTeamProfiles && (
              <button
                type="button"
                id="btn-table-team-profiles"
                onClick={onOpenTeamProfiles}
                className="inline-flex items-center px-3 py-1.5 text-xs font-bold rounded-lg text-emerald-900 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 shadow-2xs transition-all cursor-pointer"
                title="Manage Team Members & Set Incharge Profiles (PO, Drawings, Design, Production)"
              >
                <Users className="w-3.5 h-3.5 mr-1 text-emerald-600" />
                <span>Team & Profiles</span>
              </button>
            )}

            {/* Arrange & Customize Columns Toggle */}
            <div className="relative">
              <button
                type="button"
                id="btn-toggle-columns"
                onClick={() => setIsColumnPickerOpen(!isColumnPickerOpen)}
                className={`inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all shadow-2xs cursor-pointer ${
                  isColumnPickerOpen
                    ? "bg-emerald-50 border-emerald-400 text-emerald-800 ring-2 ring-emerald-100"
                    : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300"
                }`}
                title="Arrange sequence and customize visible spreadsheet columns"
              >
                <SlidersHorizontal className="w-3.5 h-3.5 mr-1.5 text-emerald-600" />
                <span>
                  Arrange Columns (
                  {Object.values(visibleColumns).filter(Boolean).length}/
                  {ALL_COLUMNS.length})
                </span>
                <ChevronDown
                  className={`w-3 h-3 ml-1 text-slate-400 transition-transform ${isColumnPickerOpen ? "rotate-180" : ""}`}
                />
              </button>

              {/* Comprehensive Column Arrange & Customize Dropdown */}
              {isColumnPickerOpen && (
                <div className="absolute right-0 top-full mt-1.5 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-2xl border border-slate-200 p-3 z-40 space-y-2.5 animate-in fade-in zoom-in-95 duration-100">
                  {/* Header */}
                  <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                    <div className="flex items-center space-x-1.5">
                      <SlidersHorizontal className="w-4 h-4 text-emerald-600" />
                      <span className="text-xs font-bold text-slate-900">
                        Arrange Columns
                      </span>
                      <span className="px-1.5 py-0.2 bg-emerald-100 text-emerald-800 text-[10px] font-bold rounded">
                        {Object.values(visibleColumns).filter(Boolean).length}/
                        {ALL_COLUMNS.length}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={resetColumnOrder}
                      className="inline-flex items-center text-[11px] text-slate-500 hover:text-emerald-700 hover:underline font-medium cursor-pointer"
                      title="Reset all columns to default sequence"
                    >
                      <RotateCcw className="w-3 h-3 mr-1" />
                      Reset Order
                    </button>
                  </div>

                  {/* Mode Navigation Tabs */}
                  <div className="flex rounded-lg bg-slate-100 p-0.5 text-xs">
                    <button
                      type="button"
                      onClick={() => setColumnPickerTab("order")}
                      className={`flex-1 py-1 px-2 rounded-md font-semibold text-center transition-colors cursor-pointer ${
                        columnPickerTab === "order"
                          ? "bg-white text-emerald-700 shadow-xs"
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                    >
                      Arrange Order & Sequence
                    </button>
                    <button
                      type="button"
                      onClick={() => setColumnPickerTab("visibility")}
                      className={`flex-1 py-1 px-2 rounded-md font-semibold text-center transition-colors cursor-pointer ${
                        columnPickerTab === "visibility"
                          ? "bg-white text-emerald-700 shadow-xs"
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                    >
                      Presets & Categories
                    </button>
                  </div>

                  {/* Search Filter for Columns */}
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
                    <input
                      type="text"
                      value={columnSearchTerm}
                      onChange={(e) => setColumnSearchTerm(e.target.value)}
                      placeholder="Filter column by name or tag..."
                      className="w-full pl-8 pr-7 py-1.5 text-xs rounded-lg border border-slate-200 focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-slate-50"
                    />
                    {columnSearchTerm && (
                      <button
                        type="button"
                        onClick={() => setColumnSearchTerm("")}
                        className="absolute right-2 top-2 text-slate-400 hover:text-slate-600 text-xs font-bold"
                      >
                        ×
                      </button>
                    )}
                  </div>

                  {/* TAB 1: ARRANGE ORDER */}
                  {columnPickerTab === "order" && (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-[10px] text-slate-500 px-1">
                        <span>Drag items or use buttons to position</span>
                        <span>Sequence from Left to Right</span>
                      </div>

                      <div className="max-h-72 overflow-y-auto space-y-1 divide-y divide-slate-100 pr-1">
                        {columnOrder
                          .map((colId, index) => ({
                            col: ALL_COLUMNS.find((c) => c.id === colId),
                            index,
                          }))
                          .filter(({ col }) => {
                            if (!col) return false;
                            if (!columnSearchTerm.trim()) return true;
                            const q = columnSearchTerm.toLowerCase();
                            return (
                              col.label.toLowerCase().includes(q) ||
                              col.category.toLowerCase().includes(q) ||
                              col.id.toLowerCase().includes(q)
                            );
                          })
                          .map(({ col, index }) => {
                            if (!col) return null;
                            const isVisible = Boolean(visibleColumns[col.id]);
                            const isFirst = index === 0;
                            const isLast = index === columnOrder.length - 1;
                            const isDragging = draggedColId === col.id;

                            return (
                              <div
                                key={col.id}
                                draggable
                                onDragStart={(e) =>
                                  handleHeaderDragStart(e, col.id)
                                }
                                onDragOver={(e) =>
                                  handleHeaderDragOver(e, col.id)
                                }
                                onDragLeave={handleHeaderDragLeave}
                                onDrop={(e) => handleHeaderDrop(e, col.id)}
                                className={`flex items-center justify-between py-1 px-1.5 rounded-lg text-xs transition-colors group ${
                                  !isVisible
                                    ? "opacity-50 bg-slate-50/70"
                                    : "hover:bg-emerald-50/50"
                                } ${isDragging ? "bg-emerald-100 opacity-40" : ""}`}
                              >
                                <div className="flex items-center space-x-2 min-w-0 flex-1">
                                  {/* Drag Handle */}
                                  <GripVertical className="w-3.5 h-3.5 text-slate-400 shrink-0 cursor-grab active:cursor-grabbing hover:text-slate-700" />

                                  {/* Order Number Badge */}
                                  <span
                                    className={`w-5 h-5 flex items-center justify-center text-[10px] font-mono font-bold rounded shrink-0 ${
                                      isVisible
                                        ? "bg-emerald-100 text-emerald-800"
                                        : "bg-slate-200 text-slate-500"
                                    }`}
                                  >
                                    {index + 1}
                                  </span>

                                  {/* Column Label & Category Tag */}
                                  <div className="min-w-0 truncate">
                                    <div className="font-medium text-slate-800 truncate text-[11px] leading-tight">
                                      {col.label}
                                    </div>
                                    <div className="text-[9px] text-slate-400 uppercase tracking-wider font-semibold">
                                      {col.category}
                                    </div>
                                  </div>
                                </div>

                                {/* Reorder Controls & Visibility Toggle */}
                                <div className="flex items-center space-x-0.5 shrink-0 ml-1">
                                  {/* Move to Top */}
                                  <button
                                    type="button"
                                    disabled={isFirst}
                                    onClick={() => moveColumn(col.id, "top")}
                                    className={`p-1 rounded text-slate-400 hover:text-emerald-700 hover:bg-slate-200 cursor-pointer ${
                                      isFirst
                                        ? "opacity-20 pointer-events-none"
                                        : ""
                                    }`}
                                    title="Move to first column"
                                  >
                                    <ChevronsUp className="w-3 h-3" />
                                  </button>

                                  {/* Move Up */}
                                  <button
                                    type="button"
                                    disabled={isFirst}
                                    onClick={() => moveColumn(col.id, "left")}
                                    className={`p-1 rounded text-slate-400 hover:text-emerald-700 hover:bg-slate-200 cursor-pointer ${
                                      isFirst
                                        ? "opacity-20 pointer-events-none"
                                        : ""
                                    }`}
                                    title="Move earlier (left)"
                                  >
                                    <ArrowUp className="w-3 h-3" />
                                  </button>

                                  {/* Move Down */}
                                  <button
                                    type="button"
                                    disabled={isLast}
                                    onClick={() => moveColumn(col.id, "right")}
                                    className={`p-1 rounded text-slate-400 hover:text-emerald-700 hover:bg-slate-200 cursor-pointer ${
                                      isLast
                                        ? "opacity-20 pointer-events-none"
                                        : ""
                                    }`}
                                    title="Move later (right)"
                                  >
                                    <ArrowDown className="w-3 h-3" />
                                  </button>

                                  {/* Move to Bottom */}
                                  <button
                                    type="button"
                                    disabled={isLast}
                                    onClick={() => moveColumn(col.id, "bottom")}
                                    className={`p-1 rounded text-slate-400 hover:text-emerald-700 hover:bg-slate-200 cursor-pointer ${
                                      isLast
                                        ? "opacity-20 pointer-events-none"
                                        : ""
                                    }`}
                                    title="Move to last column"
                                  >
                                    <ChevronsDown className="w-3 h-3" />
                                  </button>

                                  {/* Eye Visibility Toggle */}
                                  <button
                                    type="button"
                                    onClick={() => toggleColumn(col.id)}
                                    className={`p-1 rounded cursor-pointer transition-colors ${
                                      isVisible
                                        ? "text-emerald-600 hover:bg-emerald-100"
                                        : "text-slate-400 hover:text-slate-700 hover:bg-slate-200"
                                    }`}
                                    title={
                                      isVisible
                                        ? "Hide this column"
                                        : "Show this column"
                                    }
                                  >
                                    {isVisible ? (
                                      <Eye className="w-3.5 h-3.5" />
                                    ) : (
                                      <EyeOff className="w-3.5 h-3.5" />
                                    )}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  {/* TAB 2: PRESETS & CATEGORIES */}
                  {columnPickerTab === "visibility" && (
                    <div className="space-y-2">
                      {/* Presets */}
                      <div>
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                          Quick Presets
                        </div>
                        <div className="grid grid-cols-3 gap-1">
                          <button
                            type="button"
                            onClick={() => setColumnPreset("all")}
                            className="px-2 py-1 text-[11px] font-semibold bg-emerald-50 text-emerald-700 rounded-md border border-emerald-200 hover:bg-emerald-100 text-left flex items-center justify-between cursor-pointer"
                          >
                            <span>Show All</span>
                            <span className="font-mono text-[10px]">
                              {ALL_COLUMNS.length}
                            </span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setColumnPreset("engineering")}
                            className="px-2 py-1 text-[11px] font-medium bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 text-left flex items-center justify-between cursor-pointer"
                          >
                            <span>Drawing & Eng.</span>
                            <span className="font-mono text-[10px]">8</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setColumnPreset("procurement")}
                            className="px-2 py-1 text-[11px] font-medium bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 text-left flex items-center justify-between cursor-pointer"
                          >
                            <span>Vendor & PO</span>
                            <span className="font-mono text-[10px]">10</span>
                          </button>
                        </div>
                      </div>

                      {/* Column Checkboxes */}
                      <div>
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                          All Columns Checkbox
                        </div>
                        <div className="max-h-56 overflow-y-auto space-y-1 divide-y divide-slate-100 pr-1">
                          {ALL_COLUMNS.filter((col) => {
                            if (!columnSearchTerm.trim()) return true;
                            const q = columnSearchTerm.toLowerCase();
                            return (
                              col.label.toLowerCase().includes(q) ||
                              col.category.toLowerCase().includes(q)
                            );
                          }).map((col) => (
                            <label
                              key={col.id}
                              className="flex items-center justify-between py-1 px-1 rounded hover:bg-slate-50 cursor-pointer text-xs"
                            >
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  checked={!!visibleColumns[col.id]}
                                  onChange={() => toggleColumn(col.id)}
                                  className="w-3.5 h-3.5 text-emerald-600 rounded-sm border-slate-300 focus:ring-emerald-500 cursor-pointer"
                                />
                                <span className="text-slate-800 font-medium line-clamp-1">
                                  {col.label}
                                </span>
                              </div>
                              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.2 rounded bg-slate-100 text-slate-500 font-semibold">
                                {col.category}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Footer */}
                  <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
                    <span className="text-[11px] text-slate-500 font-medium">
                      Order is saved automatically
                    </span>
                    <button
                      type="button"
                      onClick={() => setIsColumnPickerOpen(false)}
                      className="px-3 py-1 text-xs font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 cursor-pointer transition-colors shadow-2xs"
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* View Mode Toggle: Master Sheet vs Cards */}
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 text-xs shadow-2xs">
              <button
                type="button"
                id="btn-view-sheet"
                onClick={() => setViewMode("sheet")}
                className={`px-3 py-1.5 rounded-md font-bold flex items-center space-x-1.5 transition-all cursor-pointer ${
                  viewMode === "sheet"
                    ? "bg-emerald-600 text-white shadow-2xs"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                }`}
                title="Full Excel-like production master sheet with all sub-details"
              >
                <TableIcon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Master Sheet</span>
              </button>
              <button
                type="button"
                id="btn-view-compact"
                onClick={() => setViewMode("compact")}
                className={`px-3 py-1.5 rounded-md font-bold flex items-center space-x-1.5 transition-all cursor-pointer ${
                  viewMode === "compact"
                    ? "bg-emerald-600 text-white shadow-2xs"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                }`}
                title="Consolidated card view"
              >
                <LayoutGrid className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Cards</span>
              </button>
            </div>

            {/* Density Toggle */}
            <button
              type="button"
              onClick={() => setIsDense(!isDense)}
              className="p-1.5 text-xs font-medium border border-slate-200 rounded-lg hover:bg-slate-100 text-slate-600"
              title={
                isDense
                  ? "Switch to Comfortable Row Height"
                  : "Switch to Compact Spreadsheet View"
              }
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Search & Filter Controls */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-2.5 pt-1">
          {/* Search Box */}
          <div className="lg:col-span-3 relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search across all columns (code, party, CA no, items)..."
              className="w-full pl-9 pr-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white shadow-2xs"
            />
          </div>

          {/* Engineer Filter */}
          <div className="lg:col-span-2">
            <select
              value={selectedEngineer}
              onChange={(e) => setSelectedEngineer(e.target.value)}
              className="w-full px-2.5 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white font-medium text-slate-700 shadow-2xs"
            >
              <option value="all">Concerned Engineer: All</option>
              {TEAM_MEMBERS.map((tm) => (
                <option key={tm.name} value={tm.name}>
                  {`${tm.name} (${tm.role})`}
                </option>
              ))}
            </select>
          </div>

          {/* Drawing Task Deadline Status Filter */}
          <div className="lg:col-span-2">
            <select
              value={selectedDrawingStatus}
              onChange={(e) => setSelectedDrawingStatus(e.target.value as any)}
              className="w-full px-2.5 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white font-medium text-slate-700 shadow-2xs"
            >
              <option value="all">Drawing Tasks: All</option>
              <option value="on_time">🟢 Drawings On Time</option>
              <option value="due_today">🟡 Drawings Due Today</option>
              <option value="overdue">🔴 Drawings Overdue</option>
              <option value="completed">🔵 Drawings Completed</option>
            </select>
          </div>

          {/* Stage Filter */}
          <div className="lg:col-span-2">
            <select
              value={selectedStage}
              onChange={(e) => setSelectedStage(e.target.value)}
              className="w-full px-2.5 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-slate-700 shadow-2xs font-medium"
            >
              <option value="all">Stage / Status: All</option>
              <optgroup label="Drawings">
                <option value="head:Drawings">Drawings (All)</option>
                <option value="Drawings:Send">Drawings: Send</option>
                <option value="Drawings:Approved">Drawings: Approved</option>
              </optgroup>
              <optgroup label="PO">
                <option value="head:PO">PO (All)</option>
                <option value="PO:Ready">PO: Ready</option>
                <option value="PO:Placed">PO: Placed</option>
              </optgroup>
              <optgroup label="Material">
                <option value="head:Material">Material (All)</option>
                <option value="Material:Not Recevied">
                  Material: Not Recevied
                </option>
                <option value="Material:Partially Recevied">
                  Material: Partially Recevied
                </option>
                <option value="Material:Fully recevied">
                  Material: Fully recevied
                </option>
              </optgroup>
              <optgroup label="Production">
                <option value="head:Production">Production (All)</option>
                <option value="Production:Cutter">Production: Cutter</option>
                <option value="Production:Laser">Production: Laser</option>
                <option value="Production:welding">Production: welding</option>
                <option value="Production:Powder coating">
                  Production: Powder coating
                </option>
                <option value="Production:Assembly">
                  Production: Assembly
                </option>
                <option value="Production:Busbar section">
                  Production: Busbar section
                </option>
                <option value="Production:Electrical">
                  Production: Electrical
                </option>
                <option value="Production:Testing">Production: Testing</option>
                <option value="Production:Ready">Production: Ready</option>
              </optgroup>
            </select>
          </div>

          {/* Priority Filter */}
          <div className="lg:col-span-1">
            <select
              value={selectedPriority}
              onChange={(e) => setSelectedPriority(e.target.value)}
              className="w-full px-2 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-slate-700 shadow-2xs"
            >
              <option value="all">Priority</option>
              <option value="Critical">Critical</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
          </div>

          {/* Team Tasks Modal Trigger Button */}
          <div className="lg:col-span-2">
            <button
              type="button"
              onClick={() => setShowTeamDeadlinesModal(true)}
              className="w-full px-2.5 py-2 text-xs font-bold bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-xl flex items-center justify-center space-x-1 transition-colors cursor-pointer shadow-2xs"
              title="View which team member has which job and deadline status"
            >
              <Users className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">Team Tasks & Deadlines</span>
            </button>
          </div>
        </div>

        {/* Active Engineer & Drawing Task Status Summary Banner */}
        {selectedEngineer !== "all" ? (
          (() => {
            const currentMetric = engineerTaskMetrics.find(
              (m) =>
                m.member.name.toLowerCase() === selectedEngineer.toLowerCase(),
            );
            if (!currentMetric) return null;
            return (
              <div className="mt-2.5 flex flex-wrap items-center justify-between gap-3 border border-slate-200 bg-white p-3">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-600 text-white flex items-center justify-center font-bold text-xs shadow-2xs">
                    {currentMetric.member.name.charAt(0)}
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="font-bold text-slate-900 text-xs sm:text-sm">
                        {currentMetric.member.name}
                      </span>
                      <span className="text-[11px] font-semibold text-emerald-700">
                        ({currentMetric.member.role})
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500">
                      Assigned to {currentMetric.totalJobs} projects • Drawing
                      tasks deadline tracking active
                    </p>
                  </div>
                </div>

                {/* Quick Status Filter Pills */}
                <div className="flex items-center flex-wrap gap-1.5">
                  <button
                    type="button"
                    onClick={() => setSelectedDrawingStatus("all")}
                    className={`px-2 py-1 rounded-md text-[11px] font-bold transition-all cursor-pointer ${
                      selectedDrawingStatus === "all"
                        ? "bg-emerald-600 text-white shadow-2xs"
                        : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    All ({currentMetric.totalJobs})
                  </button>

                  <button
                    type="button"
                    onClick={() => setSelectedDrawingStatus("on_time")}
                    className={`px-2 py-1 rounded-md text-[11px] font-bold transition-all flex items-center space-x-1 cursor-pointer ${
                      selectedDrawingStatus === "on_time"
                        ? "bg-emerald-600 text-white shadow-2xs"
                        : "bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100"
                    }`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                    <span>On Time ({currentMetric.onTime})</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setSelectedDrawingStatus("overdue")}
                    className={`px-2 py-1 rounded-md text-[11px] font-bold transition-all flex items-center space-x-1 cursor-pointer ${
                      selectedDrawingStatus === "overdue"
                        ? "bg-rose-600 text-white shadow-2xs"
                        : "bg-rose-50 text-rose-800 border border-rose-200 hover:bg-rose-100"
                    }`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                    <span>Overdue ({currentMetric.overdue})</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setSelectedDrawingStatus("completed")}
                    className={`px-2 py-1 rounded-md text-[11px] font-bold transition-all flex items-center space-x-1 cursor-pointer ${
                      selectedDrawingStatus === "completed"
                        ? "bg-emerald-600 text-white shadow-2xs"
                        : "bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100"
                    }`}
                  >
                    <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                    <span>Done ({currentMetric.completed})</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowTeamDeadlinesModal(true)}
                    className="px-2.5 py-1 rounded-md text-[11px] font-bold text-emerald-700 bg-white hover:bg-emerald-50 border border-emerald-300 ml-1 cursor-pointer"
                  >
                    Full Team Overview
                  </button>
                </div>
              </div>
            );
          })()
        ) : (
          /* General Quick Drawing Tasks Status Bar when All Engineers selected */
          <div className="mt-2.5 px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 flex flex-wrap items-center justify-between gap-2 text-xs">
            <div className="flex items-center space-x-2 text-slate-600">
              <Clock className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              <span className="font-semibold text-slate-800">
                Drawing Tasks Tracking:
              </span>
              <span className="text-slate-500 hidden sm:inline">
                Monitor which team member has which job and deadline status
              </span>
            </div>

            <div className="flex items-center space-x-1.5">
              <button
                type="button"
                onClick={() => setSelectedDrawingStatus("all")}
                className={`px-2 py-0.5 rounded text-[11px] font-bold cursor-pointer transition-all ${
                  selectedDrawingStatus === "all"
                    ? "bg-emerald-600 text-white"
                    : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-100"
                }`}
              >
                All Jobs
              </button>

              <button
                type="button"
                onClick={() => setSelectedDrawingStatus("on_time")}
                className={`px-2 py-0.5 rounded text-[11px] font-bold flex items-center space-x-1 cursor-pointer transition-all ${
                  selectedDrawingStatus === "on_time"
                    ? "bg-emerald-600 text-white"
                    : "bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100"
                }`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                <span>On Time</span>
              </button>

              <button
                type="button"
                onClick={() => setSelectedDrawingStatus("overdue")}
                className={`px-2 py-0.5 rounded text-[11px] font-bold flex items-center space-x-1 cursor-pointer transition-all ${
                  selectedDrawingStatus === "overdue"
                    ? "bg-rose-600 text-white"
                    : "bg-rose-50 text-rose-800 border border-rose-200 hover:bg-rose-100"
                }`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                <span>Overdue</span>
              </button>

              <button
                type="button"
                onClick={() => setSelectedDrawingStatus("completed")}
                className={`px-2 py-0.5 rounded text-[11px] font-bold flex items-center space-x-1 cursor-pointer transition-all ${
                  selectedDrawingStatus === "completed"
                    ? "bg-emerald-600 text-white"
                    : "bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100"
                }`}
              >
                <span>Completed</span>
              </button>

              <button
                type="button"
                onClick={() => setShowTeamDeadlinesModal(true)}
                className="inline-flex items-center space-x-1 px-2.5 py-0.5 rounded text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition-colors cursor-pointer ml-1"
                title="Open Team Member Tasks & Deadlines Matrix"
              >
                <Users className="w-3 h-3" />
                <span>Team Tasks Matrix</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Sorting, Company Sequence & Ordering Toolbar */}
      <div className="hidden flex-wrap items-center justify-between gap-3 px-4 py-2.5 bg-emerald-50/50 border-b border-slate-200 text-xs">
        <div className="flex items-center flex-wrap gap-2">
          {/* Company Sequence Toggle Button */}
          <button
            type="button"
            id="btn-toggle-company-sequence"
            onClick={() => {
              const next = !groupByCompany;
              setGroupByCompany(next);
              if (
                next &&
                sortField !== "orderConfirmDate" &&
                sortField !== "partyName"
              ) {
                setSortField("orderConfirmDate");
                setSortOrder("desc");
              }
            }}
            className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold transition-all border shadow-2xs cursor-pointer ${
              groupByCompany
                ? "bg-emerald-600 text-white border-emerald-600 shadow-emerald-200 ring-2 ring-emerald-400/20"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            }`}
            title="Group and sequence all orders by identical company"
          >
            <Building className="w-3.5 h-3.5 mr-1.5" />
            <span>
              {groupByCompany
                ? "✓ Sequence by Company (Active)"
                : "Sequence by Company (Off)"}
            </span>
          </button>

          {/* Sort by Latest Date Quick Button */}
          <button
            type="button"
            id="btn-sort-latest-date"
            onClick={() => {
              setSortField("orderConfirmDate");
              setSortOrder("desc");
            }}
            className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold transition-all border shadow-2xs cursor-pointer ${
              sortField === "orderConfirmDate" && sortOrder === "desc"
                ? "bg-emerald-600 text-white border-emerald-600 ring-2 ring-emerald-400/20"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            }`}
            title="Sort newest orders first (chronological order)"
          >
            <Calendar className="w-3.5 h-3.5 mr-1.5" />
            <span>Latest Order Date</span>
          </button>

          {/* Dedicated Collapse All Button - Available in Both Active and Off positions */}
          <button
            type="button"
            id="btn-collapse-all"
            onClick={handleCollapseAll}
            className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold transition-all border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 shadow-2xs cursor-pointer active:scale-95 hover:border-slate-400"
            title={
              groupByCompany
                ? "Collapse all company groups and row details"
                : expandedProjectIds.size > 0
                  ? "Collapse all expanded row details"
                  : "Collapse all orders into company sequence headers"
            }
          >
            <ChevronsDownUp className="w-3.5 h-3.5 mr-1.5 text-slate-500" />
            <span>Collapse All</span>
          </button>

          {/* Dedicated Expand All Button - Available in Both Active and Off positions */}
          <button
            type="button"
            id="btn-expand-all"
            onClick={handleExpandAll}
            className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold transition-all border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 shadow-2xs cursor-pointer active:scale-95 hover:border-slate-400"
            title={
              groupByCompany
                ? "Expand all company groups to reveal all orders"
                : "Expand all order row details (milestones, line items, deep logs)"
            }
          >
            <ChevronsUpDown className="w-3.5 h-3.5 mr-1.5 text-slate-500" />
            <span>Expand All</span>
          </button>

          {/* Quick Sort Presets Dropdown */}
          <div className="flex items-center space-x-1.5 bg-white border border-slate-200 rounded-lg px-2.5 py-1 shadow-2xs">
            <ListFilter className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-[11px] font-semibold text-slate-500 whitespace-nowrap">
              Sort Mode:
            </span>
            <select
              value={
                groupByCompany
                  ? sortField === "orderConfirmDate" && sortOrder === "desc"
                    ? "company_seq_latest"
                    : sortField === "partyName"
                      ? "company_seq_az"
                      : `${sortField}_${sortOrder}`
                  : sortField === "orderConfirmDate" && sortOrder === "desc"
                    ? "latest_date_flat"
                    : sortField === "orderConfirmDate" && sortOrder === "asc"
                      ? "oldest_date_flat"
                      : `${sortField}_${sortOrder}`
              }
              onChange={(e) => {
                const val = e.target.value;
                if (val === "company_seq_latest") {
                  setGroupByCompany(true);
                  setSortField("orderConfirmDate");
                  setSortOrder("desc");
                } else if (val === "company_seq_az") {
                  setGroupByCompany(true);
                  setSortField("partyName");
                  setSortOrder("asc");
                } else if (val === "latest_date_flat") {
                  setGroupByCompany(false);
                  setSortField("orderConfirmDate");
                  setSortOrder("desc");
                } else if (val === "oldest_date_flat") {
                  setGroupByCompany(false);
                  setSortField("orderConfirmDate");
                  setSortOrder("asc");
                } else if (val === "drg_send_desc") {
                  setSortField("drawingSendDate");
                  setSortOrder("desc");
                } else if (val === "drg_app_desc") {
                  setSortField("drawingApprovedDate");
                  setSortOrder("desc");
                } else if (val === "followup_desc") {
                  setSortField("lastFollowUpDate");
                  setSortOrder("desc");
                } else if (val === "code_asc") {
                  setSortField("dveplCode");
                  setSortOrder("asc");
                }
              }}
              className="text-xs font-bold text-slate-800 bg-transparent border-none focus:outline-none cursor-pointer pr-1"
            >
              <optgroup label="🏢 Same Company in Sequence">
                <option value="company_seq_latest">
                  Company Sequence (Latest Order Date First)
                </option>
                <option value="company_seq_az">
                  Company Sequence (Alphabetical A - Z)
                </option>
              </optgroup>
              <optgroup label="📅 Chronological Order">
                <option value="latest_date_flat">
                  Latest Order Date (Flat List)
                </option>
                <option value="oldest_date_flat">
                  Oldest Order Date (Flat List)
                </option>
                <option value="drg_send_desc">Latest Drawing Sent Date</option>
                <option value="drg_app_desc">
                  Latest Drawing Approved Date
                </option>
                <option value="followup_desc">
                  Latest Follow-up / Reminder Date
                </option>
              </optgroup>
              <optgroup label="🔢 Codes & Identifiers">
                <option value="code_asc">DVEPL Code / S.No (Ascending)</option>
              </optgroup>
            </select>
          </div>
        </div>

        {/* Right Stats (Clean indicator, duplicate buttons removed) */}
        <div className="flex items-center space-x-2">
          {selectedProjectIds.size > 0 && (
            <button
              type="button"
              id="btn-delete-selected-jobs-toolbar"
              onClick={() => setIsDeleteMultipleModalOpen(true)}
              className="inline-flex items-center px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold shadow-xs transition-all active:scale-95 cursor-pointer ring-2 ring-red-400/30"
              title="Delete selected jobs (Admin / PIN Security)"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />
              <span>Delete Selected ({selectedProjectIds.size})</span>
            </button>
          )}

          {groupByCompany ? (
            <span className="inline-flex items-center space-x-1.5 text-[11px] font-semibold text-slate-700 bg-white/90 px-3 py-1 rounded-lg border border-slate-200 shadow-2xs">
              <span className="w-2 h-2 rounded-full bg-emerald-600"></span>
              <strong className="text-emerald-700 font-bold">
                {companyCount}
              </strong>
              <span>Companies</span>
              <span className="text-slate-300">•</span>
              <strong className="text-slate-900 font-bold">
                {totalOrdersCount}
              </strong>
              <span>Orders</span>
            </span>
          ) : (
            <span className="inline-flex items-center space-x-1.5 text-[11px] font-semibold text-slate-700 bg-white/90 px-3 py-1 rounded-lg border border-slate-200 shadow-2xs">
              <span className="w-2 h-2 rounded-full bg-emerald-600"></span>
              <span>Flat List:</span>
              <strong className="text-slate-900 font-bold">
                {totalOrdersCount}
              </strong>
              <span>Orders</span>
              <span className="text-slate-300">•</span>
              <span className="font-mono text-emerald-700 font-bold">
                {sortField === "orderConfirmDate"
                  ? "Order Confirm Date"
                  : sortField}{" "}
                ({sortOrder.toUpperCase()})
              </span>
            </span>
          )}
        </div>
      </div>

      {/* Bulk Selection Notification Bar */}
      {selectedProjectIds.size > 0 && (
        <div className="px-4 py-2.5 bg-gradient-to-r from-red-50 via-rose-50 to-orange-50 border-b border-red-200 flex flex-wrap items-center justify-between gap-3 text-xs animate-in fade-in duration-150">
          <div className="flex items-center space-x-2.5">
            <span className="w-5 h-5 rounded-full bg-red-600 text-white flex items-center justify-center text-[10px] font-black">
              {selectedProjectIds.size}
            </span>
            <span className="font-bold text-red-950">
              {selectedProjectIds.size}{" "}
              {selectedProjectIds.size === 1 ? "Job" : "Jobs"} selected for
              action
            </span>
            <span className="text-red-300">•</span>
            <span className="text-red-800 font-medium">
              Total Value:{" "}
              <strong className="font-mono font-bold text-red-950">
                ₹
                {selectedProjectsList
                  .reduce((sum, p) => sum + (p.totalAmount || p.amount || 0), 0)
                  .toLocaleString("en-IN")}
              </strong>
            </span>
          </div>

          <div className="flex items-center space-x-2">
            <button
              type="button"
              id="btn-clear-selection"
              onClick={handleClearSelection}
              className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white hover:bg-slate-100 border border-slate-300 rounded-lg shadow-2xs transition-colors cursor-pointer"
            >
              Clear Selection
            </button>
            <button
              type="button"
              id="btn-delete-selected-jobs-banner"
              onClick={() => setIsDeleteMultipleModalOpen(true)}
              className="inline-flex items-center px-3.5 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold shadow-xs transition-all active:scale-95 cursor-pointer ring-2 ring-red-400/40"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />
              <span>
                Delete {selectedProjectIds.size} Selected{" "}
                {selectedProjectIds.size === 1 ? "Job" : "Jobs"}
              </span>
            </button>
          </div>
        </div>
      )}

      {viewMode === "compact" ? (
        <div className="p-4 bg-slate-50 border-t border-slate-200">
          {groupByCompany ? (
            <div className="space-y-4">
              {companySummaries.map((c, cIdx) => (
                <div
                  key={c.key}
                  className="bg-white rounded-xl border border-slate-200 shadow-2xs overflow-hidden"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-white p-3">
                    <div className="flex items-center space-x-2">
                      <span className="px-2 py-0.5 rounded-md text-xs font-black bg-emerald-700 text-white font-mono shadow-2xs">
                        CUSTOMER #{cIdx + 1}
                      </span>
                      <Building className="w-4 h-4 text-emerald-700" />
                      <span className="font-extrabold text-sm text-slate-900">
                        {c.partyName}
                      </span>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-emerald-600 text-white">
                        {c.count}{" "}
                        {c.count === 1 ? "Order" : "Orders in Sequence"}
                      </span>
                    </div>
                    <div className="flex items-center space-x-3 text-xs">
                      {c.latestDateStr && (
                        <span className="text-slate-600">
                          Latest Date:{" "}
                          <strong className="font-mono text-slate-800">
                            {c.latestDateStr}
                          </strong>
                        </span>
                      )}
                      <span className="font-bold text-slate-900 font-mono">
                        Total: ₹{c.totalAmount.toLocaleString("en-IN")}
                      </span>
                    </div>
                  </div>
                  <div className="p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {c.projects.map((proj, pIdx) => (
                      <div
                        key={proj.id}
                        className={`p-3 rounded-lg border transition-colors space-y-2 text-xs ${
                          selectedProjectIds.has(proj.id)
                            ? "border-red-400 bg-red-50/40 ring-1 ring-red-400"
                            : "border-slate-200 hover:border-emerald-300 bg-slate-50/50"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`card-checkbox-${proj.dveplCode}`}
                              checked={selectedProjectIds.has(proj.id)}
                              onChange={(e) =>
                                handleToggleSelectJob(
                                  proj.id,
                                  e as unknown as React.MouseEvent,
                                )
                              }
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 text-red-600 rounded border-slate-300 focus:ring-red-500 cursor-pointer"
                              title={`Select Job DVEPL-${proj.dveplCode} for bulk action`}
                            />
                            <button
                              type="button"
                              onClick={() => onOpenDetailSheet(proj)}
                              className="font-mono font-bold text-emerald-900 hover:text-emerald-700 underline decoration-emerald-300 flex items-center space-x-1 cursor-pointer"
                              title="Click to open full sheet details"
                            >
                              <span>DVEPL-{proj.dveplCode}</span>
                              <ExternalLink className="w-3 h-3" />
                            </button>
                          </div>
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] border font-semibold ${getStageBadgeColor(proj.stage)}`}
                          >
                            {proj.stage}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => onOpenDetailSheet(proj)}
                          className="text-slate-800 font-medium line-clamp-1 text-left w-full hover:text-emerald-700 hover:underline cursor-pointer"
                          title={`Click to open full sheet details: ${proj.itemDescription}`}
                        >
                          {proj.itemDescription}
                        </button>
                        {(() => {
                          const drg = getDrawingCruxBadge(proj);
                          return (
                            <div className="flex items-center justify-between text-[11px] bg-white p-2 rounded border border-slate-200">
                              <div className="font-mono text-slate-700">
                                <span className="text-slate-400 mr-1">
                                  Date:
                                </span>
                                <span className="font-bold text-slate-900">
                                  {formatToDDMMYY(proj.orderConfirmDate) || "-"}
                                </span>
                              </div>
                              <span
                                className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${drg.className}`}
                              >
                                {drg.short}
                              </span>
                            </div>
                          );
                        })()}
                        {renderCompactDocuments(proj)}
                        <div className="pt-2 border-t border-slate-200 flex items-center justify-between">
                          <span className="text-[10px] text-emerald-700 font-bold font-mono">
                            Sequence #{pIdx + 1} of {c.count}
                          </span>
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => onOpenEmailModal(proj)}
                              className="px-2 py-0.5 text-[11px] font-semibold text-white bg-emerald-600 rounded hover:bg-emerald-700 transition-colors cursor-pointer"
                            >
                              Remind
                            </button>
                            {isAdmin && (
                              <button
                                onClick={() => onOpenEditModal(proj)}
                                className="p-1 text-emerald-800 hover:text-emerald-950 hover:bg-emerald-100 rounded cursor-pointer"
                                title="Admin: Amend task"
                              >
                                <Edit className="w-3 h-3" />
                              </button>
                            )}
                            {onDeleteProject && (
                              <button
                                onClick={() => onDeleteProject(proj)}
                                className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded cursor-pointer"
                                title={
                                  isAdmin
                                    ? "Admin: Delete complete job"
                                    : "Delete complete job (Admin PIN required)"
                                }
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {flatProjects.map((proj) => (
                <div
                  key={proj.id}
                  className={`p-3 rounded-xl border shadow-2xs space-y-2 text-xs transition-colors ${
                    selectedProjectIds.has(proj.id)
                      ? "border-red-400 bg-red-50/40 ring-1 ring-red-400"
                      : "bg-white border-slate-200"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`flat-card-checkbox-${proj.dveplCode}`}
                        checked={selectedProjectIds.has(proj.id)}
                        onChange={(e) =>
                          handleToggleSelectJob(
                            proj.id,
                            e as unknown as React.MouseEvent,
                          )
                        }
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 text-red-600 rounded border-slate-300 focus:ring-red-500 cursor-pointer"
                        title={`Select Job DVEPL-${proj.dveplCode} for bulk action`}
                      />
                      <button
                        type="button"
                        onClick={() => onOpenDetailSheet(proj)}
                        className="font-mono font-bold text-emerald-900 hover:text-emerald-700 underline decoration-emerald-300 flex items-center space-x-1 cursor-pointer"
                        title="Click to open full sheet details"
                      >
                        <span>DVEPL-{proj.dveplCode}</span>
                        <ExternalLink className="w-3 h-3" />
                      </button>
                    </div>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] border font-semibold ${getStageBadgeColor(proj.stage)}`}
                    >
                      {proj.stage}
                    </span>
                  </div>
                  <div className="font-bold text-slate-900 truncate">
                    {proj.partyName}
                  </div>
                  <button
                    type="button"
                    onClick={() => onOpenDetailSheet(proj)}
                    className="text-slate-700 line-clamp-1 text-left w-full hover:text-emerald-700 hover:underline cursor-pointer"
                    title={`Click to open full sheet details: ${proj.itemDescription}`}
                  >
                    {proj.itemDescription}
                  </button>
                  {(() => {
                    const drg = getDrawingCruxBadge(proj);
                    return (
                      <div className="flex items-center justify-between text-[11px] bg-slate-50 p-2 rounded border border-slate-200">
                        <div className="font-mono text-slate-700">
                          <span className="text-slate-400 mr-1">Date:</span>
                          <span className="font-bold text-slate-900">
                            {formatToDDMMYY(proj.orderConfirmDate) || "-"}
                          </span>
                        </div>
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${drg.className}`}
                        >
                          {drg.short}
                        </span>
                      </div>
                    );
                  })()}
                  {renderCompactDocuments(proj)}
                  <div className="pt-2 border-t border-slate-100 flex items-center justify-end space-x-1.5">
                    <button
                      onClick={() => onOpenEmailModal(proj)}
                      className="px-2 py-0.5 text-[11px] font-semibold text-white bg-emerald-600 rounded hover:bg-emerald-700 cursor-pointer"
                    >
                      Remind
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => onOpenEditModal(proj)}
                        className="p-1 text-emerald-800 hover:text-emerald-950 hover:bg-emerald-100 rounded cursor-pointer"
                        title="Admin: Amend task"
                      >
                        <Edit className="w-3 h-3" />
                      </button>
                    )}
                    {onDeleteProject && (
                      <button
                        onClick={() => onDeleteProject(proj)}
                        className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded cursor-pointer"
                        title={
                          isAdmin
                            ? "Admin: Delete complete job"
                            : "Delete complete job (Admin PIN required)"
                        }
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        /* Spreadsheet View: Horizontal Scroll with Sticky Identifier Column */
        <div className="overflow-x-auto relative">
          <table className="jobs-strict-grid min-w-[1880px] table-fixed border-collapse text-left font-sans text-xs">
            <colgroup>
              <col style={{ width: SELECT_COLUMN_WIDTH }} />
              <col style={{ width: EXPAND_COLUMN_WIDTH }} />
              {visibleColumnIds.map((colId) => (
                  <col
                    key={`track-${colId}`}
                    style={{ width: COLUMN_WIDTHS[colId] || "180px" }}
                  />
              ))}
              <col style={{ width: ACTIONS_COLUMN_WIDTH }} />
            </colgroup>
            <thead>
              {/* Full Master Sheet Headers */}
              <tr className="jobs-grid-header sticky top-0 z-10 border-b border-slate-200 bg-white text-[11px] font-bold uppercase tracking-wider text-slate-700 select-none">
                {/* Select All Checkbox Header */}
                <th
                  className="py-2.5 px-2 text-center bg-slate-100/90 border-r border-slate-200"
                  style={{ width: SELECT_COLUMN_WIDTH }}
                >
                  <div className="flex items-center justify-center">
                    <input
                      type="checkbox"
                      id="checkbox-select-all-jobs"
                      checked={
                        flatProjects.length > 0 &&
                        selectedProjectIds.size === flatProjects.length
                      }
                      ref={(el) => {
                        if (el) {
                          el.indeterminate =
                            selectedProjectIds.size > 0 &&
                            selectedProjectIds.size < flatProjects.length;
                        }
                      }}
                      onChange={handleSelectAllVisible}
                      className="w-4 h-4 text-red-600 rounded border-slate-300 focus:ring-red-500 cursor-pointer"
                      title={
                        selectedProjectIds.size === flatProjects.length
                          ? "Deselect all jobs"
                          : `Select all ${flatProjects.length} jobs for bulk action`
                      }
                    />
                  </div>
                </th>

                <th
                  className="py-2.5 px-2 text-center bg-slate-100/90 border-r border-slate-200"
                  style={{ width: EXPAND_COLUMN_WIDTH }}
                >
                  <button
                    type="button"
                    id="btn-table-header-expand-collapse"
                    onClick={() => {
                      if (expandedProjectIds.size > 0) {
                        setExpandedProjectIds(new Set());
                      } else {
                        setExpandedProjectIds(
                          new Set(flatProjects.map((p) => p.id)),
                        );
                      }
                    }}
                    className="p-1 hover:bg-slate-200 rounded text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
                    title={
                      expandedProjectIds.size > 0
                        ? "Collapse all row details"
                        : "Expand all row details"
                    }
                  >
                    {expandedProjectIds.size > 0 ? (
                      <ChevronUp className="w-3.5 h-3.5 mx-auto text-emerald-600" />
                    ) : (
                      <ChevronDown className="w-3.5 h-3.5 mx-auto text-slate-400" />
                    )}
                  </button>
                </th>

                {/* Dynamic Column Headers in User-Arranged Order with Drag & Drop and Quick Arrows */}
                {visibleColumnIds.map((colId, colIdx, visibleArr) =>
                    renderColumnHeader(colId, colIdx, visibleArr.length),
                )}

                <th
                  className="py-2.5 px-3 text-right whitespace-nowrap bg-slate-100/90 border-l border-slate-200"
                  style={{ width: ACTIONS_COLUMN_WIDTH }}
                >
                  ACTIONS
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200 text-slate-800">
              {displayItems.length === 0 ? (
                <tr>
                  <td
                    colSpan={visibleColumnsCount + 3}
                    className="p-12 text-center text-slate-400"
                    data-testid="project-table-empty-state"
                  >
                    <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    {projects.length === 0
                      ? "No live projects are currently available."
                      : "No projects match the current search or filters."}
                  </td>
                </tr>
              ) : (
                displayItems.map((item, idx) => {
                  if (item.type === "header") {
                    const isCollapsed = Boolean(
                      collapsedCompanies[item.group.key],
                    );
                    const drawingStatuses = Array.from(
                      new Set(
                        item.group.projects.map(
                          (project) =>
                            getProjectStageStatus(project).drawings ||
                            "Pending",
                        ),
                      ),
                    );

                    return (
                      <tr
                        key={`company-header-${item.group.key}`}
                        className="jobs-grid-row jobs-collapsed-company-row border-b border-slate-200 bg-white transition-colors group select-none"
                      >
                        <td
                          className="border-r border-slate-200 bg-white"
                          style={{ width: SELECT_COLUMN_WIDTH }}
                        />
                        <td
                          className="border-r border-slate-200 bg-white py-2 text-center"
                          style={{ width: EXPAND_COLUMN_WIDTH }}
                        >
                          <button
                            type="button"
                            onClick={() =>
                              toggleCollapseCompany(item.group.key)
                            }
                            data-testid={`customer-group-toggle-${item.group.key}`}
                            className="text-emerald-700 transition-colors hover:text-emerald-900"
                            title={
                              isCollapsed
                                ? "Expand customer orders"
                                : "Collapse customer orders"
                            }
                          >
                            {isCollapsed ? (
                              <ChevronRight size={15} />
                            ) : (
                              <ChevronDown size={15} />
                            )}
                          </button>
                        </td>
                        {visibleColumnIds.map((colId) => {
                          if (colId === "slNo") {
                            return (
                              <td
                                key={`group-${colId}`}
                                className="border-r border-slate-200 bg-white px-0 py-2 text-center font-mono text-xs font-bold text-slate-800"
                              >
                                {item.groupIndex + 1}
                              </td>
                            );
                          }
                          if (colId === groupLabelColumnId) {
                            return (
                              <td
                                key={`group-${colId}`}
                                className="border-r border-slate-200 bg-white px-3 py-2 align-top"
                              >
                                <div className="flex items-baseline gap-2 text-xs leading-tight">
                                  <span className="font-mono text-[10px] font-extrabold uppercase tracking-wide text-emerald-700">
                                    Customer #{item.groupIndex + 1}
                                  </span>
                                  <span className="font-bold uppercase text-slate-900">
                                    {item.group.partyName}
                                  </span>
                                </div>
                                <div className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                                  {item.group.count}{" "}
                                  {item.group.count === 1 ? "ORDER" : "ORDERS"}
                                </div>
                              </td>
                            );
                          }
                          if (colId === "stage") {
                            return (
                              <td
                                key={`group-${colId}`}
                                className="border-r border-slate-200 bg-white px-3 py-2 align-top"
                              >
                                {drawingStatuses.map((status) => (
                                  <div
                                    key={status}
                                    className="text-[10px] font-semibold uppercase leading-4 text-slate-700"
                                  >
                                    {status}
                                  </div>
                                ))}
                              </td>
                            );
                          }
                          return (
                            <td
                              key={`group-${colId}`}
                              className="border-r border-slate-200 bg-white"
                            />
                          );
                        })}
                        <td
                          className="bg-white"
                          style={{ width: ACTIONS_COLUMN_WIDTH }}
                        />
                      </tr>
                    );
                  }

                  const proj = item.project;
                  const isExpanded = expandedProjectIds.has(proj.id);
                  const rowPadding = isDense ? "py-1.5" : "py-3";

                  return (
                    <React.Fragment key={proj.id}>
                      {/* Full Multi-Column Detailed Spreadsheet Row */}
                      <tr
                        onClick={(e) => {
                          const target = e.target as HTMLElement;
                          if (
                            target.closest("button") ||
                            target.closest("input") ||
                            target.closest("a") ||
                            target.closest("select")
                          ) {
                            return;
                          }
                          toggleExpand(proj.id);
                        }}
                        className={`jobs-grid-row cursor-pointer border-b border-slate-200 bg-white transition-colors hover:bg-white ${
                          selectedProjectIds.has(proj.id)
                            ? "bg-slate-50"
                            : isExpanded
                              ? "bg-white"
                              : "bg-white"
                        }`}
                        title="Click row to expand / collapse production milestones & details"
                      >
                        {/* Selection Checkbox */}
                        <td
                          className={`${rowPadding} px-2 text-center border-r border-slate-200 bg-inherit`}
                          style={{ width: SELECT_COLUMN_WIDTH }}
                        >
                          <div className="flex items-center justify-center">
                            <input
                              type="checkbox"
                              id={`checkbox-select-job-${proj.dveplCode}`}
                              checked={selectedProjectIds.has(proj.id)}
                              onChange={(e) =>
                                handleToggleSelectJob(
                                  proj.id,
                                  e as unknown as React.MouseEvent,
                                )
                              }
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 text-red-600 rounded border-slate-300 focus:ring-red-500 cursor-pointer"
                              title={`Select Job DVEPL-${proj.dveplCode} for bulk action`}
                            />
                          </div>
                        </td>

                        {/* Expand Toggle */}
                        <td
                          className={`${rowPadding} px-2 text-center border-r border-slate-200 bg-inherit`}
                          style={{ width: EXPAND_COLUMN_WIDTH }}
                        >
                          <div className="flex items-center justify-center">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpand(proj.id);
                              }}
                              data-testid={`project-row-expand-${proj.id}`}
                              className="p-1 text-slate-400 hover:text-emerald-600 rounded transition-colors cursor-pointer"
                              title="Expand production milestones & line items"
                            >
                              {isExpanded ? (
                                <ChevronDown className="w-3.5 h-3.5 text-emerald-600" />
                              ) : (
                                <ChevronRight className="w-3.5 h-3.5" />
                              )}
                            </button>
                          </div>
                        </td>

                        {/* Dynamic Columns Rendered in User-Arranged Order - Complete Details */}
                        {visibleColumnIds.map((colId) =>
                            renderColumnCell(colId, proj, rowPadding, item),
                        )}

                        {/* Action buttons (Row End Actions) */}
                        <td
                          className={`${rowPadding} px-3 text-right whitespace-nowrap border-l border-slate-200 bg-inherit`}
                          style={{ width: ACTIONS_COLUMN_WIDTH }}
                        >
                          <div className="flex items-center justify-end space-x-1.5">
                            <button
                              type="button"
                              onClick={() => onOpenDetailSheet(proj)}
                              className="px-2 py-1 text-xs font-semibold rounded-md text-slate-700 bg-white hover:bg-emerald-50 hover:text-emerald-700 border border-slate-200 shadow-2xs transition-colors cursor-pointer"
                              title="View full project tracking sheet"
                            >
                              View
                            </button>
                            <button
                              type="button"
                              onClick={() => onOpenEmailModal(proj)}
                              id={`btn-remind-${proj.dveplCode}`}
                              className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-md text-white bg-emerald-600 hover:bg-emerald-700 transition-colors shadow-2xs cursor-pointer"
                              title="Compose & send reminder email"
                            >
                              <Mail className="w-3 h-3 mr-1" />
                              <span>Remind</span>
                            </button>

                            {isAdmin && (
                              <button
                                type="button"
                                onClick={() => onOpenEditModal(proj)}
                                id={`btn-edit-${proj.dveplCode}`}
                                className="p-1 text-emerald-800 hover:text-emerald-950 hover:bg-emerald-100 rounded transition-colors cursor-pointer"
                                title="Admin: Amend task details"
                              >
                                <Edit className="w-3.5 h-3.5" />
                              </button>
                            )}

                            {onDeleteProject && (
                              <button
                                type="button"
                                onClick={() => onDeleteProject(proj)}
                                id={`btn-delete-${proj.dveplCode}`}
                                className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors cursor-pointer"
                                title={
                                  isAdmin
                                    ? "Admin: Delete complete job"
                                    : "Delete complete job (Admin PIN required)"
                                }
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>

                      {/* Expandable Milestones & Deep Log Row */}
                      {isExpanded && (
                        <tr className="jobs-expanded-row border-b border-slate-200 bg-white">
                          <td
                            colSpan={visibleColumnsCount + 3}
                            className="bg-white p-4 sm:p-5"
                          >
                            <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
                              {/* Production Milestones Checklist */}
                              <div className="md:col-span-6 bg-white p-4 rounded-xl border border-slate-200 shadow-2xs space-y-3">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-bold text-slate-900 flex items-center space-x-1.5">
                                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                    <span>
                                      Production & Milestone Checklist: DVEPL-
                                      {proj.dveplCode}
                                    </span>
                                  </span>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                  {(proj.milestones || []).map((m) => (
                                    <label
                                      key={m.id}
                                      className="flex items-start space-x-2 p-2 rounded-lg border border-slate-100 hover:bg-slate-50 cursor-pointer text-xs"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={m.completed}
                                        onChange={() =>
                                          onToggleMilestone(proj.id, m.id)
                                        }
                                        className="w-4 h-4 mt-0.5 text-emerald-600 rounded-sm border-slate-300"
                                      />
                                      <div>
                                        <div
                                          className={`font-semibold ${m.completed ? "text-slate-400 line-through" : "text-slate-800"}`}
                                        >
                                          {m.title}
                                        </div>
                                        <div className="text-[10px] text-slate-400">
                                          Assigned:{" "}
                                          {m.assignedTo || proj.concernedPerson}{" "}
                                          {m.completedAt
                                            ? `(Done ${m.completedAt})`
                                            : ""}
                                        </div>
                                      </div>
                                    </label>
                                  ))}
                                </div>
                              </div>

                              {/* Full Sheet Field Summary */}
                              <div className="md:col-span-6 bg-white p-4 rounded-xl border border-slate-200 shadow-2xs space-y-3 text-xs">
                                <span className="font-bold text-slate-900 block border-b border-slate-100 pb-1.5">
                                  Complete Sheet Column Breakdown
                                </span>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-slate-600">
                                  <div>
                                    <span className="font-semibold text-slate-800">
                                      Order Taken By:
                                    </span>{" "}
                                    {proj.orderTakenBy}
                                  </div>
                                  <div>
                                    <span className="font-semibold text-slate-800">
                                      Order Confirm Date:
                                    </span>{" "}
                                    {formatToDDMMYY(proj.orderConfirmDate)}
                                  </div>
                                  <div>
                                    <span className="font-semibold text-slate-800">
                                      Delivery Target:
                                    </span>{" "}
                                    {formatToDDMMYY(proj.deliveryTarget)}
                                  </div>
                                  <div>
                                    <span className="font-semibold text-slate-800">
                                      Contract CA No:
                                    </span>{" "}
                                    {proj.caNumber}
                                  </div>
                                  <div
                                    data-testid={`expanded-contact-details-${proj.id}`}
                                  >
                                    <span className="font-semibold text-slate-800">
                                      Contact Details:
                                    </span>{" "}
                                    {proj.contactPerson || "—"}
                                  </div>
                                  <div
                                    data-testid={`expanded-mobile-number-${proj.id}`}
                                  >
                                    <span className="font-semibold text-slate-800">
                                      Mobile Number:
                                    </span>{" "}
                                    {proj.mobileNo ? (
                                      <a
                                        href={`tel:${proj.mobileNo.replace(/[^0-9+]/g, "")}`}
                                        onClick={(event) => event.stopPropagation()}
                                        className="font-mono font-semibold text-emerald-700 hover:underline"
                                      >
                                        {proj.mobileNo}
                                      </a>
                                    ) : (
                                      "—"
                                    )}
                                  </div>
                                  <div>
                                    <span className="font-semibold text-slate-800">
                                      Drg Sent / Approved:
                                    </span>{" "}
                                    {formatToDDMMYY(proj.drawingSendDate) ||
                                      "-"}{" "}
                                    /{" "}
                                    {formatToDDMMYY(proj.drawingApprovedDate) ||
                                      "Pending"}
                                  </div>
                                  <div>
                                    <span className="font-semibold text-slate-800">
                                      Vendor / PO:
                                    </span>{" "}
                                    {proj.vendorName || "-"}{" "}
                                    {proj.poNumber ? `(#${proj.poNumber})` : ""}
                                  </div>
                                  <div>
                                    <span className="font-semibold text-slate-800">
                                      Bill No / Dispatch:
                                    </span>{" "}
                                    {proj.billNumber || "Pending"}{" "}
                                    {proj.dispatchDate
                                      ? `(${formatToDDMMYY(proj.dispatchDate)})`
                                      : ""}
                                  </div>
                                  <div>
                                    <span className="font-semibold text-slate-800">
                                      Transporter / LR:
                                    </span>{" "}
                                    {proj.transporterName || "-"}{" "}
                                    {proj.lrNumber
                                      ? `(LR: ${proj.lrNumber})`
                                      : ""}
                                  </div>
                                </div>

                                <div className="p-2 bg-slate-50 rounded-lg text-slate-700">
                                  <span className="font-semibold text-slate-900 block mb-0.5">
                                    Sheet Remark / Action Note:
                                  </span>
                                  {proj.remark || "No remark entered."}
                                </div>
                              </div>

                              {/* Line Items Schedule & Price Breakdown (Itemized Details) */}
                              {(() => {
                                const pItems = getProjectItems(proj);
                                return (
                                  <div className="md:col-span-12 bg-white p-4 rounded-xl border border-emerald-200 shadow-2xs space-y-3">
                                    <div className="flex items-center justify-between flex-wrap gap-2">
                                      <div className="flex items-center space-x-2">
                                        <Layers className="w-4 h-4 text-emerald-600" />
                                        <span className="font-extrabold text-xs text-emerald-950 uppercase tracking-wider">
                                          Equipment & Line Items Schedule
                                          (DVEPL-{proj.dveplCode})
                                        </span>
                                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                                          {pItems.length}{" "}
                                          {pItems.length === 1
                                            ? "Item"
                                            : "Distinct Items"}
                                        </span>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => onOpenDetailSheet(proj)}
                                        className="inline-flex items-center px-2.5 py-1 text-xs font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors cursor-pointer border border-emerald-200"
                                      >
                                        <ExternalLink className="w-3.5 h-3.5 mr-1" />
                                        Manage / Add Items in Sheet
                                      </button>
                                    </div>

                                    <div className="overflow-x-auto rounded-lg border border-slate-200">
                                      <table className="w-full text-left text-xs border-collapse">
                                        <thead>
                                          <tr className="bg-slate-100 text-[11px] font-bold text-slate-700 uppercase">
                                            <th className="py-2 px-3 w-12 text-center border-r border-slate-200">
                                              #
                                            </th>
                                            <th className="py-2 px-3 border-r border-slate-200">
                                              Item Name & Technical
                                              Specifications
                                            </th>
                                            <th className="py-2 px-3 text-center border-r border-slate-200 w-28">
                                              Quantity
                                            </th>
                                            <th className="py-2 px-3 text-right border-r border-slate-200 w-36">
                                              Unit Price (₹)
                                            </th>
                                            <th className="py-2 px-3 text-right w-36">
                                              Total Amount (₹)
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                          {pItems.map((it, iIdx) => {
                                            const rowTotal =
                                              (Number(it.qty) || 1) *
                                              (Number(it.unitPrice) || 0);
                                            return (
                                              <tr
                                                key={it.id || iIdx}
                                                className="hover:bg-emerald-50/20"
                                              >
                                                <td className="py-2 px-3 text-center font-mono font-bold text-slate-500 border-r border-slate-100 bg-slate-50/40">
                                                  {it.itemNumber || iIdx + 1}
                                                </td>
                                                <td className="py-2 px-3 border-r border-slate-100 font-semibold text-slate-900">
                                                  {it.name}
                                                  {it.specifications && (
                                                    <span className="block text-[11px] font-normal text-slate-500 mt-0.5">
                                                      {it.specifications}
                                                    </span>
                                                  )}
                                                </td>
                                                <td className="py-2 px-3 text-center border-r border-slate-100 font-mono font-bold text-slate-800">
                                                  {it.qty} {it.unit || "Nos"}
                                                </td>
                                                <td className="py-2 px-3 text-right font-mono text-slate-700 border-r border-slate-100">
                                                  ₹
                                                  {(
                                                    Number(it.unitPrice) || 0
                                                  ).toLocaleString("en-IN")}
                                                </td>
                                                <td className="py-2 px-3 text-right font-mono font-bold text-emerald-900 bg-emerald-50/20">
                                                  ₹
                                                  {rowTotal.toLocaleString(
                                                    "en-IN",
                                                  )}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                        <tfoot>
                                          <tr className="bg-slate-100/90 font-bold border-t border-slate-200 text-xs">
                                            <td
                                              colSpan={2}
                                              className="py-2 px-3 text-slate-700"
                                            >
                                              Total Items: {pItems.length}
                                            </td>
                                            <td className="py-2 px-3 text-center font-mono text-emerald-900">
                                              {pItems.reduce(
                                                (acc, it) =>
                                                  acc + (Number(it.qty) || 0),
                                                0,
                                              )}{" "}
                                              Units
                                            </td>
                                            <td className="py-2 px-3 text-right text-slate-600 text-[11px] uppercase">
                                              Basic Total:
                                            </td>
                                            <td className="py-2 px-3 text-right font-mono font-black text-emerald-900 text-sm">
                                              ₹
                                              {calculateItemsTotal(
                                                pItems,
                                              ).toLocaleString("en-IN")}
                                            </td>
                                          </tr>
                                        </tfoot>
                                      </table>
                                    </div>
                                  </div>
                                );
                              })()}

                              <div
                                className="md:col-span-12 border border-slate-200 bg-white p-4
                                  shadow-2xs"
                                data-testid={`project-documents-section-${proj.id}`}
                              >
                                <div
                                  className="flex flex-wrap items-center justify-between gap-2 border-b
                                    border-slate-100 pb-2.5"
                                >
                                  <div className="flex items-center gap-2">
                                    <FileText className="h-4 w-4 text-emerald-600" />
                                    <span
                                      className="text-xs font-extrabold uppercase tracking-wide
                                        text-slate-900"
                                    >
                                      Documents & Uploads
                                    </span>
                                  </div>
                                  <span
                                    className="font-mono text-[10px] font-bold text-emerald-800"
                                    data-testid={`project-documents-count-${proj.id}`}
                                  >
                                    {(proj.documents || []).length} file
                                    {(proj.documents || []).length === 1 ? "" : "s"}
                                  </span>
                                </div>

                                {(proj.documents || []).length === 0 ? (
                                  <p
                                    className="py-4 text-xs text-slate-500"
                                    data-testid={`project-documents-empty-${proj.id}`}
                                  >
                                    No uploaded files are linked to this job.
                                  </p>
                                ) : (
                                  <>
                                    <div className="mt-3 space-y-2 md:hidden">
                                      {(proj.documents || []).map((document) => {
                                        const status =
                                          document.status || "Pending review";
                                        const statusClass =
                                          status === "Approved"
                                            ? "text-emerald-800"
                                            : status === "Rejected"
                                              ? "text-rose-700"
                                              : "text-amber-800";

                                        return (
                                          <div
                                            key={`mobile-${document.id}`}
                                            className="border border-slate-200 bg-white p-3"
                                            data-testid={
                                              `project-document-mobile-row-${proj.id}-${document.id}`
                                            }
                                          >
                                            <div className="flex items-start justify-between gap-3">
                                              <div className="min-w-0">
                                                <div
                                                  className="text-[10px] font-bold uppercase tracking-wide
                                                    text-slate-500"
                                                  data-testid={
                                                    `project-document-mobile-folder-${proj.id}-${document.id}`
                                                  }
                                                >
                                                  {document.folder || "General"}
                                                </div>
                                                <div
                                                  className="mt-1 break-words text-xs font-bold
                                                    text-slate-900"
                                                  data-testid={
                                                    `project-document-mobile-name-${proj.id}-${document.id}`
                                                  }
                                                >
                                                  {document.name}
                                                </div>
                                              </div>
                                              <span
                                                className={`shrink-0 text-[11px] font-semibold ${statusClass}`}
                                                data-testid={
                                                  `project-document-mobile-status-${proj.id}-${document.id}`
                                                }
                                              >
                                                {status}
                                              </span>
                                            </div>
                                            <div
                                              className="mt-2 font-mono text-[10px] text-slate-600"
                                              data-testid={
                                                `project-document-mobile-uploaded-at-${proj.id}-${document.id}`
                                              }
                                            >
                                              Uploaded {formatToDDMMYY(document.uploadedAt) || "—"}
                                            </div>
                                            <div className="mt-3 flex flex-wrap gap-2">
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  onOpenDocument?.(proj, document)
                                                }
                                                className="inline-flex items-center gap-1 border
                                                  border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px]
                                                  font-bold text-emerald-800 hover:bg-emerald-100"
                                                data-testid={
                                                  `project-document-mobile-open-${proj.id}-${document.id}`
                                                }
                                              >
                                                <Eye className="h-3.5 w-3.5" />
                                                Open
                                              </button>
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  onDownloadDocument?.(proj, document)
                                                }
                                                className="inline-flex items-center gap-1 border
                                                  border-slate-200 bg-white px-2 py-1 text-[11px]
                                                  font-bold text-slate-700 hover:bg-slate-50"
                                                data-testid={
                                                  `project-document-mobile-download-${proj.id}-${document.id}`
                                                }
                                              >
                                                <Download className="h-3.5 w-3.5" />
                                                Download
                                              </button>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                    <div className="hidden overflow-x-auto md:block">
                                      <table
                                        className="w-full min-w-[680px] border-collapse text-left text-xs"
                                      >
                                      <thead>
                                        <tr
                                          className="border-b border-slate-200 text-[10px] font-bold
                                            uppercase tracking-wide text-slate-500"
                                        >
                                          <th className="px-2 py-2">Folder</th>
                                          <th className="px-2 py-2">File name</th>
                                          <th className="px-2 py-2">Uploaded</th>
                                          <th className="px-2 py-2">Status</th>
                                          <th className="px-2 py-2 text-right">Actions</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(proj.documents || []).map((document) => {
                                          const status =
                                            document.status || "Pending review";
                                          const statusClass =
                                            status === "Approved"
                                              ? "text-emerald-800"
                                              : status === "Rejected"
                                                ? "text-rose-700"
                                                : "text-amber-800";

                                          return (
                                            <tr
                                              key={document.id}
                                              className="border-b border-slate-100 last:border-b-0"
                                              data-testid={
                                                `project-document-row-${proj.id}-${document.id}`
                                              }
                                            >
                                              <td
                                                className="px-2 py-2 font-medium text-slate-700"
                                                data-testid={
                                                  `project-document-folder-${proj.id}-${document.id}`
                                                }
                                              >
                                                {document.folder || "General"}
                                              </td>
                                              <td
                                                className="max-w-[280px] px-2 py-2 font-medium
                                                  text-slate-900"
                                                data-testid={
                                                  `project-document-name-${proj.id}-${document.id}`
                                                }
                                              >
                                                <span
                                                  className="block truncate"
                                                  title={document.name}
                                                >
                                                  {document.name}
                                                </span>
                                              </td>
                                              <td
                                                className="px-2 py-2 font-mono text-slate-600"
                                                data-testid={
                                                  `project-document-uploaded-at-${proj.id}-${document.id}`
                                                }
                                              >
                                                {formatToDDMMYY(document.uploadedAt) || "—"}
                                              </td>
                                              <td
                                                className={`px-2 py-2 font-semibold ${statusClass}`}
                                                data-testid={
                                                  `project-document-status-${proj.id}-${document.id}`
                                                }
                                              >
                                                {status}
                                              </td>
                                              <td className="px-2 py-2 text-right">
                                                <div className="inline-flex items-center gap-1">
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      onOpenDocument?.(proj, document)
                                                    }
                                                    className="inline-flex items-center gap-1 border
                                                      border-emerald-200 bg-emerald-50 px-2 py-1
                                                      text-[11px] font-bold text-emerald-800
                                                      hover:bg-emerald-100"
                                                    data-testid={
                                                      `project-document-open-${proj.id}-${document.id}`
                                                    }
                                                  >
                                                    <Eye className="h-3.5 w-3.5" />
                                                    Open
                                                  </button>
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      onDownloadDocument?.(proj, document)
                                                    }
                                                    className="inline-flex items-center gap-1 border
                                                      border-slate-200 bg-white px-2 py-1 text-[11px]
                                                      font-bold text-slate-700 hover:bg-slate-50"
                                                    data-testid={
                                                      `project-document-download-${proj.id}-${document.id}`
                                                    }
                                                  >
                                                    <Download className="h-3.5 w-3.5" />
                                                    Download
                                                  </button>
                                                </div>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                      </table>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Quick Expanded Actions Bar */}
                            <div className="mt-4 pt-3 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 text-xs">
                              <div className="text-slate-500 font-medium">
                                Job Ref:{" "}
                                <span className="font-mono text-slate-800 font-bold">
                                  DVEPL-{proj.dveplCode || proj.sNo}
                                </span>{" "}
                                • Client:{" "}
                                <span className="font-bold text-slate-800">
                                  {proj.partyName}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  type="button"
                                  onClick={() => onOpenDetailSheet(proj)}
                                  className="px-3 py-1 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg transition-colors cursor-pointer"
                                >
                                  Open Full Tracking Sheet
                                </button>
                                {onDeleteProject && (
                                  <button
                                    type="button"
                                    onClick={() => onDeleteProject(proj)}
                                    className="px-3 py-1 text-xs font-semibold text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors inline-flex items-center space-x-1 cursor-pointer"
                                    title={
                                      isAdmin
                                        ? "Admin: Delete complete job"
                                        : "Delete complete job (Admin PIN required)"
                                    }
                                  >
                                    <Trash2 className="w-3.5 h-3.5 mr-1" />
                                    <span>Delete Complete Job</span>
                                  </button>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Team Tasks & Deadlines Tracking Modal */}
      <TeamTasksDeadlinesModal
        isOpen={showTeamDeadlinesModal}
        onClose={() => setShowTeamDeadlinesModal(false)}
        projects={projects}
        onSelectProject={onOpenDetailSheet}
        onFilterEngineerInTable={(eng) => setSelectedEngineer(eng)}
      />

      {/* Quick Add Core Incharge Role Modal */}
      <QuickAddRoleModal
        isOpen={!!quickAddRoleProject}
        onClose={() => setQuickAddRoleProject(null)}
        project={quickAddRoleProject}
        onAddRole={onAddProjectRole || (() => {})}
      />

      {/* Delete Multiple Jobs Confirmation & Security Modal */}
      <DeleteMultipleJobsModal
        isOpen={isDeleteMultipleModalOpen}
        onClose={() => setIsDeleteMultipleModalOpen(false)}
        onConfirmDeleteMultiple={handleDeleteSelectedProjects}
        selectedProjects={selectedProjectsList}
        isAdmin={Boolean(isAdmin)}
        onDeselectJob={(id) => handleToggleSelectJob(id)}
      />
    </div>
  );
};
